// src/ConferenceRegistration.jsx
import React, { useEffect, useState, useRef } from "react";
import Select from "react-select";
//import "../index.css";
import "../ConferenceRegistration.css";
import TermsAndConditions from "./TermsAndConditions";

const currentHost = window.location.host;
const TOTAL_STEPS = 6;
const MOBILE_MAX_LENGTH = 10;

// Read keys and URLs from .env
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY;
const REGISTRATION_BASE_URL =
  process.env.REACT_APP_REGISTRATION_API_URL || "http://localhost:8085";
const PAYMENT_BASE_URL =
  process.env.REACT_APP_PAYMENT_API_URL || "http://localhost:8085";
const CONFERENCE_API =
  process.env.REACT_APP_CONFERENCE_API_URL ||
  "http://localhost:8080/conference";
const USER_API_URL =
  process.env.REACT_APP_USER_API_URL || "http://localhost:8085/registration";

// Log environment variables for debugging
console.log("🔧 Environment Variables:");
console.log("  REGISTRATION_BASE_URL:", REGISTRATION_BASE_URL);
console.log("  PAYMENT_BASE_URL:", PAYMENT_BASE_URL);
console.log("  CONFERENCE_API:", CONFERENCE_API);

// Updated to use v2 API endpoints
const CONFERENCE_REGISTRATION_URL = `${REGISTRATION_BASE_URL}/registration`;
const CHECK_REGISTRATION_URL = `${REGISTRATION_BASE_URL}/registration/check-registration`;
const ORDER_URL = `${PAYMENT_BASE_URL}/payment/create-order`;
const PAYMENT_VERIFY_URL = `${PAYMENT_BASE_URL}/payment/verify`;
const PAYMENT_SAVE_URL = `${PAYMENT_BASE_URL}/payment/save-with-breakdown`; // Updated to use breakdown endpoint

const DEFAULT_COUNTRIES = [
  "India",
  "United States",
  "United Kingdom",
  "Australia",
  "Canada",
].map((name) => ({
  label: name,
  value: name,
}));

const ProgressBar = ({ step }) => {
  const steps = [
    "Personal",
    "Address",
    "Affiliation",
    "Category",
    "Summary",
    "Payment",
  ];
  return (
    <div className="progress-bar">
      {steps.map((label, i) => {
        const idx = i + 1;
        const status =
          step === idx ? "current" : step > idx ? "done" : "upcoming";
        return (
          <div key={i} className={`progress-step ${status}`}>
            <div className="progress-circle" data-title={label}>
              {idx}
            </div>
            {/* <div className="progress-label">{label}</div>
            {i < steps.length - 1 && <div className="progress-line" />} */}
          </div>
        );
      })}
    </div>
  );
};

const ConferenceRegistration = ({
  existingUser,
  prefilledEmail,
  onBackToLogin,
}) => {
  const [step, setStep] = useState(1);
  const [bannerUrl, setBannerUrl] = useState(null);
  const [categories, setCategories] = useState([]); // Array of {id, value}
  const [registrationTypes, setRegistrationTypes] = useState([]); // Array of {id, value}
  const [departments, setDepartments] = useState([]); // Array of {id, value}
  const [countries, setCountries] = useState([]);
  const [errors, setErrors] = useState({
    email: "",
    mobile: "",
    firstName: "",
    lastName: "",
    country: "",
  });
  const [saving, setSaving] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [paymentInitiated, setPaymentInitiated] = useState(false);
  const [paymentErrorShown, setPaymentErrorShown] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(!!existingUser);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsError, setTermsError] = useState("");
  const [conferenceId, setConferenceId] = useState(null);
  const [conferenceName, setConferenceName] = useState(null);
  const [registrationFee, setRegistrationFee] = useState(null);
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeError, setFeeError] = useState(null);
  const [studentIdCardFile, setStudentIdCardFile] = useState(null);
  const [studentIdCardError, setStudentIdCardError] = useState("");

  // Accompanying persons state
  const [accompanyingPersons, setAccompanyingPersons] = useState([]);
  const [accompanyingPersonFee, setAccompanyingPersonFee] = useState(null);

  // Focused sessions state
  const [registerForFocusedSessions, setRegisterForFocusedSessions] =
    useState("No");
  const [focusedSessions, setFocusedSessions] = useState([]);
  const [selectedFocusedSessions, setSelectedFocusedSessions] = useState([]);
  const [focusedSessionFees, setFocusedSessionFees] = useState({}); // Store fees by session ID

  // Gender options state
  const [genderOptions, setGenderOptions] = useState([]);

  // Track if registration has been updated (for payment validation)
  const [registrationJustUpdated, setRegistrationJustUpdated] = useState(false);

  // State to preserve category-specific data when switching categories
  const [savedMembershipNumber, setSavedMembershipNumber] = useState("");
  const [savedStudentIdCard, setSavedStudentIdCard] = useState(null);
  const [savedStudentIdCardName, setSavedStudentIdCardName] = useState("");
  const [savedStudentIdCardType, setSavedStudentIdCardType] = useState("");

  // Ref to track Razorpay instance and prevent duplicate error handling
  const razorpayInstanceRef = useRef(null);
  const paymentFailureHandledRef = useRef(false);
  const razorpayOpenRef = useRef(false);

  // Ref to track if checkExistingRegistration has loaded conference data
  const conferenceDataLoadedRef = useRef(false);

  // Initialize form data with existing user data or prefilled email
  const initializeFormData = () => {
    if (existingUser) {
      return {
        id: existingUser.id || "",
        title: existingUser.title || "",
        firstName: existingUser.firstName || "",
        lastName: existingUser.lastName || "",
        email: existingUser.email || "",
        mobile: existingUser.phone || "",
        gender: existingUser.gender || "",
        country: existingUser.country || "",
        state: existingUser.state || "",
        city: existingUser.city || "",
        pincode: existingUser.zipCode || "",
        address: existingUser.address || "",
        affiliation: existingUser.affiliation || "",
        institution: existingUser.institution || "",
        fullNameCertificate: existingUser.getFullName
          ? existingUser.getFullName()
          : `${existingUser.firstName || ""} ${
              existingUser.lastName || ""
            }`.trim(),
        mciRegistered: "No",
        mciNumber: "",
        department: "",
        departmentId: null,
        otherDepartment: "",
        category: "",
        categoryId: null,
        registrationType: "",
        registrationTypeId: null,
        membershipNumber: "",
        isaMembershipNumber: "",
        studentIdCard: null,
        studentIdCardName: "",
        studentIdCardType: "",
        prefix: "",
        conference: "",
      };
    } else {
      return {
        id: "",
        title: "",
        firstName: "",
        lastName: "",
        email: prefilledEmail || "",
        mobile: "",
        gender: "",
        country: "",
        state: "",
        city: "",
        pincode: "",
        address: "",
        affiliation: "",
        institution: "",
        fullNameCertificate: "",
        mciRegistered: "No",
        mciNumber: "",
        department: "",
        departmentId: null,
        otherDepartment: "",
        category: "",
        categoryId: null,
        registrationType: "",
        registrationTypeId: null,
        membershipNumber: "",
        isaMembershipNumber: "",
        studentIdCard: null,
        studentIdCardName: "",
        studentIdCardType: "",
        prefix: "",
        conference: "",
      };
    }
  };

  const [formData, setFormData] = useState(initializeFormData());

  // Update form data when existingUser prop changes (e.g., after login)
  // This useEffect should ONLY update user profile fields, NOT conference-specific fields
  // Conference fields will be populated by checkExistingRegistration
  useEffect(() => {
    if (existingUser && existingUser.email) {
      setFormData((prev) => {
        // Only update user profile if user fields are empty OR if they match the existing user
        // This prevents overwriting when user navigates back from editing
        const shouldUpdate = !prev.email || prev.email === existingUser.email;

        if (!shouldUpdate) {
          return prev;
        }

        // If conference data has been loaded by checkExistingRegistration, don't overwrite it
        if (conferenceDataLoadedRef.current) {
          return prev;
        }

        // ONLY update user profile fields - DO NOT touch conference-specific fields
        // Let checkExistingRegistration handle those
        const updated = {
          ...prev, // Start with all existing fields
          // Update ONLY user profile fields
          id: existingUser.id || prev.id || "",
          title: existingUser.title || prev.title || "",
          firstName: existingUser.firstName || prev.firstName || "",
          lastName: existingUser.lastName || prev.lastName || "",
          email: existingUser.email || prev.email || "",
          mobile: existingUser.phone || prev.mobile || "",
          gender: existingUser.gender || prev.gender || "",
          country: existingUser.country || prev.country || "",
          state: existingUser.state || prev.state || "",
          city: existingUser.city || prev.city || "",
          pincode: existingUser.zipCode || prev.pincode || "",
          address: existingUser.address || prev.address || "",
          affiliation: existingUser.affiliation || prev.affiliation || "",
          institution: existingUser.institution || prev.institution || "",
          fullNameCertificate: existingUser.getFullName
            ? existingUser.getFullName()
            : prev.fullNameCertificate ||
              `${existingUser.firstName || ""} ${
                existingUser.lastName || ""
              }`.trim(),
          // DO NOT include conference fields here - they will be set by checkExistingRegistration
        };

        return updated;
      });
      setIsExistingUser(true);

      // Note: Navigation to step 5 will be handled by checkExistingRegistration
      // after conference data is loaded
    }
  }, [existingUser]);

  // Update form data when prefilledEmail prop changes (e.g., new user registration)
  useEffect(() => {
    if (prefilledEmail && !existingUser) {
      setFormData((prev) => ({
        ...prev,
        email: prefilledEmail,
      }));
      setIsExistingUser(false);
    }
  }, [prefilledEmail, existingUser]);

  // Initialize saved category-specific values on first load or when formData has initial values
  useEffect(() => {
    if (formData.membershipNumber && formData.category === "ISCCM(Members)") {
      setSavedMembershipNumber(formData.membershipNumber);
    }
    if (
      formData.studentIdCard &&
      (formData.category === "Student" || formData.category === "student")
    ) {
      setSavedStudentIdCard(formData.studentIdCard);
      setSavedStudentIdCardName(formData.studentIdCardName);
      setSavedStudentIdCardType(formData.studentIdCardType);
    }
  }, []); // Run only once on mount

  // Debug effect to monitor formData.paid changes
  useEffect(() => {}, [formData.paid]);

  // Reset paymentInitiated flag when navigating to/from Step 6
  // This prevents the button from being stuck in "Payment Initiated" state
  useEffect(() => {
    if (step === 6 && paymentInitiated) {
      setPaymentInitiated(false);
    }
  }, [step]);

  // Basic handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    let nextValue = value;

    if (name === "mobile") {
      nextValue = value.replace(/[^0-9]/g, "");
      if (nextValue.length > MOBILE_MAX_LENGTH) {
        nextValue = nextValue.slice(0, MOBILE_MAX_LENGTH);
      }
    }

    setFormData((p) => ({ ...p, [name]: nextValue }));

    if (["email", "mobile", "firstName", "lastName"].includes(name)) {
      validateField(name, nextValue, {
        value: nextValue,
        country: name === "mobile" ? formData.country : undefined,
      });
    }

    // Clear student ID card error if category or registration type changes away from student
    if (name === "category" || name === "registrationType") {
      const newCategory = name === "category" ? nextValue : formData.category;
      const newRegType =
        name === "registrationType" ? nextValue : formData.registrationType;

      const isStudentCategory =
        newCategory && newCategory.toLowerCase().includes("student");
      const isStudentRegType =
        newRegType && newRegType.toLowerCase().includes("student");

      // If neither is student anymore, clear the error
      if (!isStudentCategory && !isStudentRegType) {
        setStudentIdCardError("");
      }
    }
  };

  // Handle student ID card file upload
  const handleStudentIdCardUpload = async (e) => {
    // Only allow single file upload
    if (e.target.files.length > 1) {
      setStudentIdCardError("Please upload only one file");
      e.target.value = ""; // Clear the input
      return;
    }

    const file = e.target.files[0];

    if (!file) {
      setStudentIdCardFile(null);
      setStudentIdCardError("");
      setFormData((prev) => ({
        ...prev,
        studentIdCard: null,
        studentIdCardName: "",
        studentIdCardType: "",
      }));
      return;
    }

    // Validate file type (JPEG or PDF only)
    const allowedTypes = ["image/jpeg", "image/jpg", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      setStudentIdCardError("Please upload only JPEG or PDF files");
      e.target.value = ""; // Clear the input
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      setStudentIdCardError("File size must be less than 5MB");
      e.target.value = ""; // Clear the input
      return;
    }

    // File is valid - convert to base64 and store in state
    // Will be uploaded to database when registration is saved
    setStudentIdCardError("");
    setStudentIdCardFile(file);

    try {
      // Convert file to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;

        // Store file data in state (will be sent to backend when saving registration)
        setFormData((prev) => ({
          ...prev,
          studentIdCard: base64String, // Store base64 data
          studentIdCardName: file.name,
          studentIdCardType: file.type,
        }));

        console.log("Student ID card loaded, size:", file.size, "bytes");
      };

      reader.onerror = () => {
        console.error("Error reading file");
        setStudentIdCardError("Failed to read file. Please try again.");
        e.target.value = "";
        setStudentIdCardFile(null);
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error processing student ID card:", error);
      setStudentIdCardError("Failed to process file. Please try again.");
      e.target.value = ""; // Clear the input
      setStudentIdCardFile(null);
    }
  };

  const handleCountryChange = (opt) => {
    const nextCountry = opt ? opt.value : "";
    setFormData((p) => ({ ...p, country: nextCountry }));
    validateField("country", nextCountry);
    if (formData.mobile) {
      validateField("mobile", formData.mobile, {
        country: nextCountry,
        value: formData.mobile,
      });
    }
  };

  // Accompanying persons handlers
  const handleAddAccompanyingPerson = () => {
    setAccompanyingPersons([
      ...accompanyingPersons,
      {
        name: "",
        paid: false,
        paymentId: null,
        feeTypeId: accompanyingPersonFee?.id, // Set feeTypeId when adding
      },
    ]);
    setRegistrationJustUpdated(false); // Reset flag since user added a new item
  };

  const handleRemoveAccompanyingPerson = (index) => {
    const updated = accompanyingPersons.filter((_, i) => i !== index);
    setAccompanyingPersons(updated);
    setRegistrationJustUpdated(false); // Reset flag since user modified items
  };

  const handleAccompanyingPersonNameChange = (index, value) => {
    const updated = [...accompanyingPersons];
    updated[index] = { ...updated[index], name: value }; // Preserve existing fields
    setAccompanyingPersons(updated);
    setRegistrationJustUpdated(false); // Reset flag since user modified items
  };

  // Focused sessions handlers
  const handleFocusedSessionsChange = (value) => {
    setRegisterForFocusedSessions(value);
    if (value === "Yes" && focusedSessions.length === 0) {
      // Fetch focused sessions when user selects "Yes"
      fetchFocusedSessions();
    }
    if (value === "No") {
      // Clear selected sessions when user selects "No"
      setSelectedFocusedSessions([]);
    }
  };

  const handleFocusedSessionCheckboxChange = async (sessionId) => {
    setRegistrationJustUpdated(false); // Reset flag since user modified focused sessions
    setSelectedFocusedSessions((prev) => {
      if (prev.includes(sessionId)) {
        // Remove if already selected
        // Also remove the fee from the fees object
        setFocusedSessionFees((prevFees) => {
          const newFees = { ...prevFees };
          delete newFees[sessionId];
          return newFees;
        });
        return prev.filter((id) => id !== sessionId);
      } else {
        // Add if not selected - fetch the fee
        fetchFocusedSessionFee(sessionId).then((feeData) => {
          if (feeData) {
            setFocusedSessionFees((prevFees) => ({
              ...prevFees,
              [sessionId]: feeData,
            }));
          }
        });
        return [...prev, sessionId];
      }
    });
  };

  // Enhanced validation helper functions
  const validateEmailAdvanced = (email) => {
    if (!email) return "Email is required.";

    // Basic pattern check
    const emailPattern =
      /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!emailPattern.test(email)) {
      return "Please enter a valid email address format.";
    }

    // Check for disposable email domains
    const disposableDomains = [
      "10minutemail.com",
      "tempmail.org",
      "guerrillamail.com",
      "mailinator.com",
      "trashmail.com",
      "0-mail.com",
      "throwaway.email",
      "temp-mail.org",
      "yopmail.com",
      "emailondeck.com",
      "tempail.com",
      "sharklasers.com",
    ];

    const domain = email.split("@")[1]?.toLowerCase();
    if (disposableDomains.includes(domain)) {
      return "Please use a permanent email address, not a temporary one.";
    }

    // Check for proper domain structure
    if (domain) {
      // Domain should have at least one dot and valid TLD
      const domainParts = domain.split(".");
      if (domainParts.length < 2) {
        return "Email domain must have a valid top-level domain.";
      }

      // Check if TLD is reasonable (2-6 characters)
      const tld = domainParts[domainParts.length - 1];
      if (tld.length < 2 || tld.length > 6) {
        return "Email domain has an invalid top-level domain.";
      }

      // Check for consecutive dots
      if (domain.includes("..")) {
        return "Email address cannot contain consecutive dots.";
      }
    }

    // Check email length
    if (email.length > 254) {
      return "Email address is too long.";
    }

    // Check local part (before @) length
    const localPart = email.split("@")[0];
    if (localPart.length > 64) {
      return "Email address local part is too long.";
    }

    return "";
  };

  const validateMobileAdvanced = (mobile, country) => {
    const normalized = mobile.replace(/[\s()-+]/g, "");
    if (!normalized) return "Mobile number is required.";

    // Check for obviously invalid patterns
    if (!/^\d+$/.test(normalized)) {
      return "Mobile number can only contain digits.";
    }

    // Check for sequential or repeated numbers (like 1111111111 or 1234567890)
    const isSequential = (num) => {
      for (let i = 1; i < num.length; i++) {
        if (parseInt(num[i]) !== parseInt(num[i - 1]) + 1) return false;
      }
      return true;
    };

    const isRepeated = (num) => {
      return num.split("").every((digit) => digit === num[0]);
    };

    if (isRepeated(normalized) || isSequential(normalized)) {
      return "Please enter a valid mobile number.";
    }

    // Country-specific validation
    if (country === "India") {
      // Remove country code if present
      const indianNumber = normalized.replace(/^(?:91|0)/, "");

      if (indianNumber.length !== 10) {
        return "Indian mobile number must be 10 digits.";
      }

      // Check if starts with valid digits (6,7,8,9)
      if (!/^[6-9]/.test(indianNumber)) {
        return "Indian mobile number must start with 6, 7, 8, or 9.";
      }

      // Check for valid operator codes
      const validPrefixes = [
        "60",
        "61",
        "62",
        "63",
        "64",
        "65",
        "66",
        "67",
        "68",
        "69",
        "70",
        "71",
        "72",
        "73",
        "74",
        "75",
        "76",
        "77",
        "78",
        "79",
        "80",
        "81",
        "82",
        "83",
        "84",
        "85",
        "86",
        "87",
        "88",
        "89",
        "90",
        "91",
        "92",
        "93",
        "94",
        "95",
        "96",
        "97",
        "98",
        "99",
      ];

      const prefix = indianNumber.substring(0, 2);
      if (!validPrefixes.includes(prefix)) {
        return "Please enter a valid Indian mobile number.";
      }
    } else if (country === "United States") {
      // Remove country code if present
      const usNumber = normalized.replace(/^1/, "");

      if (usNumber.length !== 10) {
        return "US mobile number must be 10 digits.";
      }

      // Check area code (first 3 digits) - shouldn't start with 0 or 1
      if (/^[01]/.test(usNumber)) {
        return "US phone number area code cannot start with 0 or 1.";
      }

      // Check exchange code (next 3 digits) - shouldn't start with 0 or 1
      const exchange = usNumber.substring(3, 6);
      if (/^[01]/.test(exchange)) {
        return "US phone number exchange code cannot start with 0 or 1.";
      }
    } else if (country === "United Kingdom") {
      // UK mobile numbers start with 07 and are 11 digits total
      const ukNumber = normalized.replace(/^44/, "");

      if (!ukNumber.startsWith("07") || ukNumber.length !== 11) {
        return "UK mobile number must start with 07 and be 11 digits.";
      }
    } else if (country === "Australia") {
      // Australian mobile numbers start with 04 and are 10 digits total
      const auNumber = normalized.replace(/^61/, "");

      if (!auNumber.startsWith("04") || auNumber.length !== 10) {
        return "Australian mobile number must start with 04 and be 10 digits.";
      }
    } else if (country === "Canada") {
      // Same as US format
      const caNumber = normalized.replace(/^1/, "");

      if (caNumber.length !== 10) {
        return "Canadian mobile number must be 10 digits.";
      }

      if (/^[01]/.test(caNumber)) {
        return "Canadian phone number area code cannot start with 0 or 1.";
      }
    } else {
      // Generic international validation
      if (normalized.length < 7 || normalized.length > 15) {
        return "Mobile number must be between 7 and 15 digits.";
      }

      // Should not start with 00 (typically used for international dialing)
      if (normalized.startsWith("00")) {
        return "Please enter the mobile number without international dialing codes.";
      }
    }

    return "";
  };

  const validateField = (field, rawValue, overrides = {}) => {
    if (
      !["email", "mobile", "firstName", "lastName", "country"].includes(field)
    )
      return true;

    const valueSource = overrides.value ?? rawValue;
    const value = typeof valueSource === "string" ? valueSource.trim() : "";
    const country = overrides.country ?? formData.country;
    let error = "";

    if (field === "firstName") {
      if (!value) {
        error = "First name is required.";
      } else if (value.length < 2) {
        error = "First name must be at least 2 characters.";
      } else if (!/^[a-zA-Z\s.'-]+$/.test(value)) {
        error =
          "First name can only contain letters, spaces, dots, apostrophes, and hyphens.";
      }
    }

    if (field === "lastName") {
      if (!value) {
        error = "Last name is required.";
      } else if (value.length < 1) {
        error = "Last name must be at least 2 characters.";
      } else if (!/^[a-zA-Z\s.'-]+$/.test(value)) {
        error =
          "Last name can only contain letters, spaces, dots, apostrophes, and hyphens.";
      }
    }

    if (field === "country") {
      if (!value) {
        error = "Country is required.";
      }
    }

    if (field === "email") {
      error = validateEmailAdvanced(value);
    }

    if (field === "mobile") {
      error = validateMobileAdvanced(value, country);
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const validateAllMandatoryFields = () => {
    const firstNameValid = validateField("firstName", formData.firstName);
    const lastNameValid = validateField("lastName", formData.lastName);
    const emailValid = validateField("email", formData.email);
    const mobileValid = validateField("mobile", formData.mobile, {
      country: formData.country,
      value: formData.mobile,
    });
    const countryValid = validateField("country", formData.country);
    return (
      firstNameValid &&
      lastNameValid &&
      emailValid &&
      mobileValid &&
      countryValid
    );
  };

  // Check if user is already registered for this conference and auto-populate data
  const checkExistingRegistration = async (email, conferenceNameParam) => {
    if (!email) {
      return;
    }

    try {
      // Always fetch user's registration data to check what they have
      const fetchUrl = `${REGISTRATION_BASE_URL}/registration/user/${encodeURIComponent(
        email
      )}/${encodeURIComponent(conferenceNameParam)}`;

      const dataResponse = await fetch(fetchUrl);

      if (dataResponse.ok) {
        const registrations = await dataResponse.json();

        // Debug each registration's conference field
        registrations.forEach((reg, index) => {});

        // Find the registration for this specific conference
        // Compare against conference name (from API), not currentHost (which is hostname like localhost:3000)

        const existingReg = registrations.find(
          (reg) => reg.conference === conferenceNameParam && !reg.cancelled
        );

        if (existingReg && existingReg.user) {
          // Debug category and registration type fields from API

          // Debug payment status fields

          // Determine payment status based on actual API response structure
          // Priority: Use 'paid' field from backend if available, otherwise check payment records
          const isPaid =
            existingReg.paid === true ||
            (existingReg.payment &&
              Array.isArray(existingReg.payment) &&
              existingReg.payment.length > 0 &&
              existingReg.payment.some(
                (p) => p.status === "SUCCESS" || p.status === "COMPLETED"
              )) ||
            existingReg.confirmationDate !== null;

          // Debug the actual fields that exist in the API response

          if (existingReg.payment) {
          }

          // Test each condition separately

          if (existingReg.payment) {
          }

          // Auto-populate form with existing registration data
          setFormData((prev) => {
            // Mark that conference data has been loaded
            conferenceDataLoadedRef.current = true;

            const newFormData = {
              ...prev,
              // User details from existing registration
              email: existingReg.user.email,
              firstName: existingReg.user.firstName || "",
              lastName: existingReg.user.lastName || "",
              mobile: existingReg.user.phone || "",
              gender: existingReg.user.gender || "",
              country: existingReg.user.country || "",
              state: existingReg.user.state || "",
              city: existingReg.user.city || "",
              address: existingReg.user.address || "",
              pincode: existingReg.user.zipCode || "",
              affiliation: existingReg.affiliation || "",
              institution: existingReg.institution || "",
              // Conference details from existing registration
              conference: existingReg.conference,
              category: existingReg.category || "",
              categoryId: existingReg.categoryId || null,
              registrationType: existingReg.registrationType || "",
              registrationTypeId: existingReg.registrationTypeId || null,
              membershipNumber: existingReg.membershipNumber || "",
              isaMembershipNumber: existingReg.isaMembershipNumber || "",
              department: existingReg.department || "",
              departmentId: existingReg.departmentId || null,
              otherDepartment: existingReg.otherDepartment || "",
              // Student ID - only set if there's actually a file stored
              studentIdCard: existingReg.studentIdURL
                ? existingReg.studentIdURL
                : null,
              studentIdCardUploaded: existingReg.studentIdURL ? true : false, // Only mark as uploaded if there's a URL
              // MCI details from existing registration
              mciRegistered: existingReg.mciRegistered || "No",
              mciNumber: existingReg.mciNumber || "",
              specialRequirements: existingReg.specialRequirements || "",
              // Payment status - use calculated value (no confirmed field in database)
              paid: isPaid,
              // Set existing registration IDs for future operations
              id: existingReg.registrationId,
              regId: existingReg.id,
              // Store additional fees for display in step-5 and protection in step-4
              additionalFees:
                existingReg.additionalFees &&
                Array.isArray(existingReg.additionalFees)
                  ? existingReg.additionalFees
                  : [],
              // Store zero payments (free items) for display in step-5
              zeroPayments:
                existingReg.zeroPayments &&
                Array.isArray(existingReg.zeroPayments)
                  ? existingReg.zeroPayments
                  : [],
              // Store payment data with breakdown (if available)
              payment:
                existingReg.payment && Array.isArray(existingReg.payment)
                  ? existingReg.payment
                  : [],
            };

            console.log("💾 Loaded zeroPayments:", existingReg.zeroPayments);

            return newFormData;
          });

          console.log("🔄 Loading existing registration data...");
          console.log(
            "  existingReg.additionalFees:",
            existingReg.additionalFees
          );
          console.log("  existingReg.zeroPayments:", existingReg.zeroPayments);
          console.log("  accompanyingPersonFee:", accompanyingPersonFee);

          // Load accompanying persons and focused sessions from additionalFees
          const storedAdditionalFees =
            existingReg.additionalFees &&
            Array.isArray(existingReg.additionalFees)
              ? existingReg.additionalFees
              : [];

          console.log("📦 Stored additional fees:", storedAdditionalFees);

          // Load zero payments once for both accompanying persons and focused sessions
          const storedZeroPayments =
            existingReg.zeroPayments && Array.isArray(existingReg.zeroPayments)
              ? existingReg.zeroPayments
              : [];

          console.log("📦 Stored zero payments:", storedZeroPayments);

          if (storedAdditionalFees.length > 0 && accompanyingPersonFee) {
            // Separate accompanying persons from focused sessions
            // Load from BOTH additionalFees AND zeroPayments
            const accompanyingPersonsFromAdditionalFees = storedAdditionalFees
              .filter((fee) => fee.feeTypeId === accompanyingPersonFee.id)
              .map((fee) => {
                console.log(
                  "  - Accompanying person from additionalFees:",
                  fee
                );
                return {
                  name: fee.name,
                  paymentId: fee.paymentId,
                  paid: !!fee.paymentId,
                  feeTypeId: fee.feeTypeId,
                  id: fee.id,
                };
              });

            const accompanyingPersonsFromZeroPayments = storedZeroPayments
              .filter((zp) => zp.feeTypeId === accompanyingPersonFee.id)
              .map((zp) => {
                console.log("  - Accompanying person from zeroPayments:", zp);
                return {
                  name: zp.name,
                  paymentId: null, // Zero payments don't have paymentId
                  paid: true, // But they are saved in the database
                  feeTypeId: zp.feeTypeId,
                  id: zp.id,
                };
              });

            const accompanyingPersonsList = [
              ...accompanyingPersonsFromAdditionalFees,
              ...accompanyingPersonsFromZeroPayments,
            ];

            console.log(
              "👥 Accompanying persons from additionalFees:",
              accompanyingPersonsFromAdditionalFees
            );
            console.log(
              "👥 Accompanying persons from zeroPayments:",
              accompanyingPersonsFromZeroPayments
            );
            console.log(
              "👥 Combined accompanying persons:",
              accompanyingPersonsList
            );

            if (accompanyingPersonsList.length > 0) {
              setAccompanyingPersons(accompanyingPersonsList);
            }

            // Load focused sessions (non-accompanying person fees) from BOTH additionalFees AND zeroPayments
            const focusedSessionsFromAdditionalFees = storedAdditionalFees
              .filter((fee) => fee.feeTypeId !== accompanyingPersonFee.id)
              .map((fee) => fee.feeTypeId);

            const focusedSessionsFromZeroPayments = storedZeroPayments
              .filter((zp) => zp.feeTypeId !== accompanyingPersonFee.id)
              .map((zp) => zp.feeTypeId);

            // Combine both lists and remove duplicates
            const focusedSessionsList = [
              ...new Set([
                ...focusedSessionsFromAdditionalFees,
                ...focusedSessionsFromZeroPayments,
              ]),
            ];

            console.log(
              "🎯 Focused sessions from additional fees:",
              focusedSessionsFromAdditionalFees
            );
            console.log(
              "🎯 Focused sessions from zero payments:",
              focusedSessionsFromZeroPayments
            );
            console.log("🎯 Combined focused sessions:", focusedSessionsList);

            if (focusedSessionsList.length > 0) {
              setRegisterForFocusedSessions("Yes");
              setSelectedFocusedSessions(focusedSessionsList);
              console.log(
                "✅ Set selectedFocusedSessions to:",
                focusedSessionsList
              );
              console.log("📍 conferenceId available?", conferenceId);

              // Fetch fees for these sessions (needed to display rates)
              // Wait a bit if conferenceId is not available yet
              if (conferenceId) {
                console.log("✅ conferenceId is available, fetching fees now");
                focusedSessionsList.forEach((sessionId) => {
                  fetchFocusedSessionFee(sessionId).then((feeData) => {
                    console.log(
                      `💰 Fetched fee for session ${sessionId}:`,
                      feeData
                    );
                    if (feeData) {
                      setFocusedSessionFees((prevFees) => ({
                        ...prevFees,
                        [sessionId]: feeData,
                      }));
                    }
                  });
                });
              } else {
                console.warn(
                  "⚠️ conferenceId not available yet, will fetch fees later"
                );
              }
            } else {
              console.log("⚠️ No focused sessions to load (list is empty)");
            }
          } else if (storedAdditionalFees.length > 0) {
            // Fallback: if accompanyingPersonFee not loaded yet, map all as accompanying persons
            console.log(
              "⚠️ accompanyingPersonFee not loaded, mapping all as accompanying persons"
            );
            const accompanyingPersonsList = storedAdditionalFees.map((fee) => ({
              name: fee.name,
              paymentId: fee.paymentId,
              paid: !!fee.paymentId,
              feeTypeId: fee.feeTypeId,
              id: fee.id,
            }));

            if (accompanyingPersonsList.length > 0) {
              setAccompanyingPersons(accompanyingPersonsList);
            }
          }

          // Navigate directly to summary page for existing conference registration
          setTimeout(() => {
            setStep(5);
            /*  if (!isPaid) {
              alert(`Welcome back! Your registration has been loaded. You can update your information and complete payment.`);
            } else {
              alert(`Welcome back! Your registration for this conference is already completed.`);
            }*/
          }, 500);
        } else if (registrations.length > 0) {
          // User has registrations for other conferences, just populate personal details
          const anyRegistration = registrations[0];
          if (anyRegistration && anyRegistration.user) {
            console.log("🔄 User has registration for OTHER conference");
            console.log("  Other conference:", anyRegistration.conference);
            console.log("  Current conference:", conferenceNameParam);
            console.log(
              "  CRITICAL: Clearing id and regId for NEW conference registration"
            );

            // Only update fields that are empty to preserve data from existingUser prop
            setFormData((prev) => ({
              ...prev,
              // Only populate fields if they're empty (preserve data from login)
              email: prev.email || anyRegistration.user.email,
              firstName: prev.firstName || anyRegistration.user.firstName || "",
              lastName: prev.lastName || anyRegistration.user.lastName || "",
              mobile: prev.mobile || anyRegistration.user.phone || "",
              country: prev.country || anyRegistration.user.country || "",
              state: prev.state || anyRegistration.user.state || "",
              city: prev.city || anyRegistration.user.city || "",
              address: prev.address || anyRegistration.user.address || "",
              pincode: prev.pincode || anyRegistration.user.zipCode || "",
              affiliation:
                prev.affiliation || anyRegistration.affiliation || "",
              institution:
                prev.institution || anyRegistration.institution || "",
              // CRITICAL: Clear registration IDs so new conference creates POST, not PUT
              id: null,
              regId: null,
              // Also clear conference-specific fields
              conference: conferenceNameParam,
              category: "",
              categoryId: null,
              registrationType: "",
              registrationTypeId: null,
            }));
          }

          // Navigate logged-in user to summary page to complete conference-specific details

          setTimeout(() => {
            setStep(5);
          }, 300);
        } else {
          // No registrations found for this conference - treat as new user
          // User data should already be populated from existingUser prop
          console.log("✨ NEW conference registration for existing user");
          console.log("  Conference:", conferenceNameParam);
          console.log(
            "  CRITICAL: Clearing id and regId for NEW conference registration"
          );

          // CRITICAL: Clear registration IDs so new conference creates POST, not PUT
          setFormData((prev) => ({
            ...prev,
            id: null,
            regId: null,
            conference: conferenceNameParam,
            // Clear conference-specific fields
            category: "",
            categoryId: null,
            registrationType: "",
            registrationTypeId: null,
          }));

          // Navigate to step 1 to start fresh registration process

          setTimeout(() => {
            setStep(1);
          }, 300);
        }
      } else {
        console.error(
          "Failed to fetch registration data:",
          dataResponse.status
        );
        // Still navigate logged-in user to summary page even if fetch fails
        setTimeout(() => {
          setStep(5);
        }, 300);
      }
    } catch (error) {
      console.error("Error checking existing registration:", error);
      // Don't block the user if the check fails - navigate to summary page
      setTimeout(() => {
        setStep(5);
      }, 300);
    }
  };

  // Check existing registration only when existingUser changes (from login)
  useEffect(() => {
    if (existingUser && existingUser.email && conferenceName) {
      // Longer delay to ensure conference data is loaded first
      setTimeout(() => {
        checkExistingRegistration(existingUser.email, conferenceName);
      }, 1000);
    } else if (existingUser && existingUser.email && !conferenceName) {
    }
  }, [existingUser?.email, conferenceName]); // Run when existingUser email OR conferenceName changes

  // Process additional fees and zero payments when accompanyingPersonFee is loaded
  useEffect(() => {
    // Only process if we have formData with fees AND accompanyingPersonFee is now loaded
    if (
      !accompanyingPersonFee ||
      !formData.additionalFees ||
      formData.additionalFees.length === 0
    ) {
      return;
    }

    // CRITICAL: Don't overwrite user's manual changes
    // Only auto-populate on initial load (when accompanyingPersons is empty)
    // If user has already modified the list, don't overwrite it
    if (accompanyingPersons.length > 0) {
      return; // User has data in state, don't overwrite
    }

    // Check if we've already processed focused sessions
    // Only check selectedFocusedSessions - let accompanying persons be reprocessed if needed
    if (selectedFocusedSessions.length > 0) {
      return; // Already processed
    }

    const storedAdditionalFees = formData.additionalFees || [];
    const storedZeroPayments = formData.zeroPayments || [];

    // Separate accompanying persons from focused sessions
    const accompanyingPersonsFromAdditionalFees = storedAdditionalFees
      .filter((fee) => fee.feeTypeId === accompanyingPersonFee.id)
      .map((fee) => ({
        name: fee.name,
        paymentId: fee.paymentId,
        paid: !!fee.paymentId,
        feeTypeId: fee.feeTypeId,
        id: fee.id,
      }));

    const accompanyingPersonsFromZeroPayments = storedZeroPayments
      .filter((zp) => zp.feeTypeId === accompanyingPersonFee.id)
      .map((zp) => ({
        name: zp.name,
        paymentId: null,
        paid: true,
        feeTypeId: zp.feeTypeId,
        id: zp.id,
      }));

    const accompanyingPersonsList = [
      ...accompanyingPersonsFromAdditionalFees,
      ...accompanyingPersonsFromZeroPayments,
    ];

    if (accompanyingPersonsList.length > 0) {
      setAccompanyingPersons(accompanyingPersonsList);
    }

    // Load focused sessions
    const focusedSessionsFromAdditionalFees = storedAdditionalFees
      .filter((fee) => fee.feeTypeId !== accompanyingPersonFee.id)
      .map((fee) => fee.feeTypeId);

    const focusedSessionsFromZeroPayments = storedZeroPayments
      .filter((zp) => zp.feeTypeId !== accompanyingPersonFee.id)
      .map((zp) => zp.feeTypeId);

    const focusedSessionsList = [
      ...new Set([
        ...focusedSessionsFromAdditionalFees,
        ...focusedSessionsFromZeroPayments,
      ]),
    ];

    if (focusedSessionsList.length > 0) {
      setRegisterForFocusedSessions("Yes");
      setSelectedFocusedSessions(focusedSessionsList);

      // Fetch fees for these sessions (needed to display rates)
      focusedSessionsList.forEach((sessionId) => {
        fetchFocusedSessionFee(sessionId).then((feeData) => {
          if (feeData) {
            setFocusedSessionFees((prevFees) => ({
              ...prevFees,
              [sessionId]: feeData,
            }));
          }
        });
      });
    }
  }, [accompanyingPersonFee, formData.additionalFees, formData.zeroPayments]); // Run when accompanyingPersonFee loads

  // Fetch focused session fees when conferenceId becomes available
  useEffect(() => {
    // If we have selected focused sessions but no fees loaded yet, and now conferenceId is available
    if (conferenceId && selectedFocusedSessions.length > 0) {
      // Check which sessions don't have fees loaded yet
      const sessionsNeedingFees = selectedFocusedSessions.filter(
        (sessionId) => !focusedSessionFees[sessionId]
      );

      if (sessionsNeedingFees.length > 0) {
        sessionsNeedingFees.forEach((sessionId) => {
          fetchFocusedSessionFee(sessionId).then((feeData) => {
            if (feeData) {
              setFocusedSessionFees((prevFees) => ({
                ...prevFees,
                [sessionId]: feeData,
              }));
            }
          });
        });
      }
    }
  }, [conferenceId, selectedFocusedSessions, focusedSessionFees]); // Run when conferenceId, selectedFocusedSessions, or focusedSessionFees changes

  // Fetch focused sessions list when "Yes" is selected (either manually or from loaded data)
  useEffect(() => {
    if (
      registerForFocusedSessions === "Yes" &&
      conferenceId &&
      focusedSessions.length === 0
    ) {
      fetchFocusedSessions();
    }
  }, [registerForFocusedSessions, conferenceId, focusedSessions.length]);

  // Fetch gender options when conference ID is available
  useEffect(() => {
    if (conferenceId) {
      fetchGenderOptions();
    }
  }, [conferenceId]);

  // Fetch initial data
  useEffect(() => {
    fetch(`${CONFERENCE_API}/${currentHost}/json`)
      .then((r) => r.json())
      .then((data) => {
        if (data.bannerUrl) setBannerUrl(data.bannerUrl);
        if (data.prefix) {
          setFormData((p) => ({ ...p, prefix: data.prefix }));
        } else {
        }

        // Store conference name for registration
        if (data.name) {
          setConferenceName(data.name);
          setFormData((p) => ({ ...p, conference: data.name }));
        }

        // Extract conference ID and use it for subsequent API calls
        const confId = data.id;
        if (confId) {
          setConferenceId(confId); // Store conference ID in state

          // Fetch categories using conference ID - store full objects with id and value
          fetch(`${CONFERENCE_API}/${confId}/master/type/category/details`)
            .then((r) => r.json())
            .then((categoryData) => {
              // Store full objects: {id, value}
              const categoryObjects = Array.isArray(categoryData)
                ? categoryData
                : [];
              setCategories(categoryObjects);
            })
            .catch(() => setCategories([]));

          // Fetch registration types using conference ID - store full objects with id and value
          fetch(
            `${CONFERENCE_API}/${confId}/master/type/conferenceType/details`
          )
            .then((r) => r.json())
            .then((regData) => {
              // Store full objects: {id, value}
              const regTypeObjects = Array.isArray(regData) ? regData : [];
              setRegistrationTypes(regTypeObjects);
            })
            .catch(() => setRegistrationTypes([]));

          // Fetch departments using conference ID - store full objects with id and value
          fetch(`${CONFERENCE_API}/${confId}/master/type/Department/details`)
            .then((r) => r.json())
            .then((deptData) => {
              // Store full objects: {id, value}
              const deptObjects = Array.isArray(deptData) ? deptData : [];
              setDepartments(deptObjects);
            })
            .catch(() => setDepartments([]));
        }
      })
      .catch((error) => {
        console.error("Failed to fetch conference data:", error);
      });

    fetch("https://restcountries.com/v3.1/all?fields=name")
      .then((r) => r.json())
      .then((data) => {
        const list = (Array.isArray(data) ? data : [])
          .map((c) => {
            const name =
              typeof c?.name === "string"
                ? c.name
                : c?.name?.common ||
                  c?.name?.official ||
                  c?.name?.nativeName ||
                  null;
            if (!name) return null;
            return { label: name, value: name };
          })
          .filter(Boolean)
          .sort((a, b) => a.label.localeCompare(b.label));
        setCountries(list.length ? list : DEFAULT_COUNTRIES);
      })
      .catch(() => setCountries(DEFAULT_COUNTRIES));
  }, []);

  // Fetch registration fee based on category, registration type, and current date
  const fetchRegistrationFee = async () => {
    // Only fetch if we have at least conferenceId and categoryId
    // registrationTypeId is optional
    if (!conferenceId || !formData.categoryId) {
      setRegistrationFee(null);
      return;
    }

    setFeeLoading(true);
    setFeeError(null);

    try {
      // Get current date in YYYY-MM-DD format
      const today = new Date().toISOString().split("T")[0];

      // Fetch all fees valid for today's date for this conference
      // The backend will filter fees where current date is between valid_from and valid_to
      const feeUrl = `${CONFERENCE_API}/fees/conference/${conferenceId}/valid-on?date=${today}`;

      const response = await fetch(feeUrl);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error("No fee configuration found for the current date");
        }
        throw new Error("Failed to fetch registration fee");
      }

      const validFees = await response.json();

      // Filter fees by participantTypeId (category) and optionally eventTypeId (registration type)
      // If registrationTypeId is provided, match both; otherwise match only by category
      const matchingFee = validFees.find(
        (fee) =>
          fee.participantType?.id === formData.categoryId &&
          fee.isActive === true &&
          (formData.registrationTypeId
            ? fee.eventType?.id === formData.registrationTypeId // Match both if registrationTypeId exists
            : !fee.eventType || fee.eventType === null) // Match category-only fees if no registrationTypeId
      );

      if (!matchingFee) {
        const errorMsg = formData.registrationTypeId
          ? "No fee configuration found for the selected category and registration type"
          : "No fee configuration found for the selected category";
        throw new Error(errorMsg);
      }

      setRegistrationFee(matchingFee);
      setFeeError(null);
    } catch (error) {
      console.error("Error fetching registration fee:", error);
      setFeeError(error.message);
      setRegistrationFee(null);
    } finally {
      setFeeLoading(false);
    }
  };

  // Fetch accompanying person fee
  const fetchAccompanyingPersonFee = async () => {
    if (!conferenceId) {
      setAccompanyingPersonFee(null);
      return;
    }

    try {
      const feeUrl = `${CONFERENCE_API}/fees/conference/${conferenceId}/AccompanyingFee`;

      const response = await fetch(feeUrl);

      if (!response.ok) {
        console.error("❌ Failed to fetch accompanying person fee");
        console.error("Status:", response.status);
        console.error("Status Text:", response.statusText);
        setAccompanyingPersonFee(null);
        return;
      }

      const feeData = await response.json();

      // Check if response is an array or single object
      if (Array.isArray(feeData)) {
        // Find the active accompanying person fee
        const accompanyingFee = feeData.find((fee) => fee.isActive === true);

        if (accompanyingFee) {
          setAccompanyingPersonFee(accompanyingFee);
        } else {
          setAccompanyingPersonFee(null);
        }
      } else if (feeData && typeof feeData === "object") {
        // Single object response

        if (feeData.isActive === true || feeData.isActive === undefined) {
          setAccompanyingPersonFee(feeData);
        } else {
          setAccompanyingPersonFee(null);
        }
      } else {
        setAccompanyingPersonFee(null);
      }
    } catch (error) {
      console.error("Error fetching accompanying person fee:", error);
      setAccompanyingPersonFee(null);
    }
  };

  // Fetch focused sessions from API
  const fetchFocusedSessions = async () => {
    if (!conferenceId) {
      setFocusedSessions([]);
      return;
    }

    try {
      const sessionsUrl = `${CONFERENCE_API}/${conferenceId}/master/type/FocussedSessions/details`;

      const response = await fetch(sessionsUrl);

      if (!response.ok) {
        console.error("❌ Failed to fetch focused sessions");
        console.error("Status:", response.status);
        console.error("Status Text:", response.statusText);
        setFocusedSessions([]);
        return;
      }

      const sessionsData = await response.json();

      // Store the sessions array
      const sessionsArray = Array.isArray(sessionsData) ? sessionsData : [];
      setFocusedSessions(sessionsArray);
    } catch (error) {
      console.error("Error fetching focused sessions:", error);
      setFocusedSessions([]);
    }
  };

  // Fetch gender options
  const fetchGenderOptions = async () => {
    if (!conferenceId) {
      setGenderOptions([]);
      return;
    }

    try {
      const genderUrl = `${CONFERENCE_API}/${conferenceId}/master/type/gender/details`;
      console.log("🔍 Fetching gender options from:", genderUrl);

      const response = await fetch(genderUrl);

      if (!response.ok) {
        console.error("❌ Failed to fetch gender options");
        console.error("Status:", response.status);
        setGenderOptions([]);
        return;
      }

      const genderData = await response.json();
      console.log("📦 Gender options received:", genderData);

      // Store the gender options array
      const genderArray = Array.isArray(genderData) ? genderData : [];
      setGenderOptions(genderArray);
    } catch (error) {
      console.error("Error fetching gender options:", error);
      setGenderOptions([]);
    }
  };

  // Fetch focused session fee by session ID
  const fetchFocusedSessionFee = async (sessionId) => {
    if (!conferenceId || !sessionId) {
      return null;
    }

    try {
      const feeUrl = `${CONFERENCE_API}/fees/conference/${conferenceId}/fee/${sessionId}`;
      console.log(
        `🔍 Fetching focused session fee for session ${sessionId} from:`,
        feeUrl
      );

      const response = await fetch(feeUrl);

      if (!response.ok) {
        console.error(
          `❌ Failed to fetch fee for focused session ${sessionId}`
        );
        console.error("Status:", response.status);
        console.error("Status Text:", response.statusText);
        return null;
      }

      const feeData = await response.json();
      console.log(
        `📦 Raw API response for session ${sessionId}:`,
        JSON.stringify(feeData, null, 2)
      );
      console.log(`💰 Fee rate extracted:`, feeData?.rate);
      console.log(`💵 Fee currency extracted:`, feeData?.currency);

      // Return the fee data
      return feeData;
    } catch (error) {
      console.error(
        `Error fetching focused session fee for ${sessionId}:`,
        error
      );
      return null;
    }
  };

  // Fetch fee when category or registration type changes
  // registrationTypeId is optional - fee can be fetched with just category
  useEffect(() => {
    if (conferenceId && formData.categoryId) {
      fetchRegistrationFee();
    }
    // Also fetch accompanying person fee when conference is loaded
    if (conferenceId) {
      fetchAccompanyingPersonFee();
    }
  }, [conferenceId, formData.categoryId, formData.registrationTypeId]);

  // Step navigation with field validation
  const validateCurrentStep = (currentStep) => {
    const requiredFields = [];

    if (currentStep === 1) {
      if (!formData.firstName.trim()) requiredFields.push("First Name");
      if (!formData.lastName.trim()) requiredFields.push("Last Name");
      if (!formData.email.trim()) requiredFields.push("Email");
      if (!formData.mobile.trim()) requiredFields.push("Mobile");

      // Also validate email and mobile format for step 1
      const emailValid = validateField("email", formData.email);
      const mobileValid = validateField("mobile", formData.mobile, {
        country: formData.country,
        value: formData.mobile,
      });

      if (!emailValid || !mobileValid) {
        return false;
      }
    }

    if (currentStep === 2) {
      if (!formData.country.trim()) requiredFields.push("Country");
      if (!formData.city.trim()) requiredFields.push("City");
      if (!formData.pincode.trim()) requiredFields.push("Pincode");
      if (!formData.address.trim()) requiredFields.push("Address");
    }

    if (currentStep === 3) {
      if (!formData.affiliation.trim()) requiredFields.push("Affiliation");
      if (!formData.institution.trim())
        requiredFields.push("Institution or Hospital Name");
      // Check if MCI Number is required when "Yes" is selected
      if (formData.mciRegistered === "Yes" && !formData.mciNumber.trim()) {
        requiredFields.push("MCI Number");
      }
    }

    if (currentStep === 4) {
      // Only require fields that have data configured
      if (categories.length > 0 && !formData.category.trim()) {
        requiredFields.push("Category");
      }
      if (registrationTypes.length > 0 && !formData.registrationType.trim()) {
        requiredFields.push("Registration Type");
      }
      // Check if membership number is required
      if (
        formData.category === "Delegate (Members)" &&
        !formData.membershipNumber.trim()
      ) {
        requiredFields.push("Membership Number");
      }
      // Check if ISA membership number is required (only for ISA MEMBER, not NON-ISA MEMBER)
      if (
        formData.category &&
        formData.category.toString().toLowerCase().includes("isa") &&
        !formData.category.toString().toLowerCase().includes("non") &&
        !formData.category.toString().toLowerCase().includes("isccm") &&
        (!formData.isaMembershipNumber || !formData.isaMembershipNumber.trim())
      ) {
        requiredFields.push("ISA Membership Number");
      }
      // Check if student ID card is required - check both category and registration type
      const isStudentCategory =
        formData.category &&
        formData.category.toLowerCase().includes("student");
      const isStudentRegistrationType =
        formData.registrationType &&
        formData.registrationType.toLowerCase().includes("student");

      console.log("🎓 Student validation check:");
      console.log("  Category:", formData.category);
      console.log("  Registration Type:", formData.registrationType);
      console.log("  isStudentCategory:", isStudentCategory);
      console.log("  isStudentRegistrationType:", isStudentRegistrationType);
      console.log("  formData.studentIdCard:", formData.studentIdCard);
      console.log("  studentIdCard type:", typeof formData.studentIdCard);

      // Student ID card is required if either category or registration type includes "student"
      const isStudentRegistration =
        isStudentCategory || isStudentRegistrationType;

      // Check if student ID card is present
      // Valid states:
      // 1. Base64 data (new upload): starts with "data:"
      // 2. Database marker: value is "DATABASE"
      // 3. Database URL: value starts with "http" or has a path
      // Invalid states: null, undefined, empty string, or any other value
      const hasValidStudentIdCard =
        formData.studentIdCard &&
        formData.studentIdCard !== null &&
        formData.studentIdCard !== "" &&
        (formData.studentIdCard.startsWith("data:") || // New upload (base64)
          formData.studentIdCard === "DATABASE" || // Previously uploaded to DB
          formData.studentIdCard.startsWith("http") || // URL from DB
          formData.studentIdCard.includes("/")); // File path from DB

      console.log("  hasValidStudentIdCard:", hasValidStudentIdCard);

      if (isStudentRegistration && !hasValidStudentIdCard) {
        console.log(
          "  ❌ VALIDATION FAILED: Student ID Card is required but missing!"
        );
        requiredFields.push("Student ID Card");
        // Set error state for visual feedback
        setStudentIdCardError(
          "Student ID Card is required for student registrations"
        );
      } else if (!isStudentRegistration) {
        // Clear error if no longer a student
        console.log("  ✅ Not a student registration - clearing error");
        setStudentIdCardError("");
      } else {
        console.log("  ✅ Student ID Card validation passed - file present");
        setStudentIdCardError("");
      }

      // Validate accompanying persons - if added, name is mandatory
      accompanyingPersons.forEach((person, index) => {
        if (!person.name || !person.name.trim()) {
          requiredFields.push(`Accompanying Person ${index + 1} Name`);
        }
      });
    }

    console.log("📋 Validation complete for step", currentStep);
    console.log("  Required fields missing:", requiredFields.length);
    if (requiredFields.length > 0) {
      console.log("  Missing fields:", requiredFields);
    }

    if (requiredFields.length > 0) {
      alert(
        `Please fill in the following required fields: ${requiredFields.join(
          ", "
        )}`
      );
      return false;
    }

    console.log("✅ All validations passed for step", currentStep);
    return true;
  };

  const next = () => {
    if (!validateCurrentStep(step)) {
      return;
    }

    // Special validation before proceeding to payment step (step 6)
    if (step === 5) {
      // Moving from summary to payment
      if (!formData.id && !formData.regId) {
        alert(
          "Please save your registration before proceeding to payment. Click the 'Save' button first."
        );
        return;
      }

      // Check if terms and conditions are agreed
      if (!agreedToTerms) {
        setTermsError(
          "You must agree to the Terms & Conditions before proceeding to payment."
        );
        // Scroll to terms checkbox
        const termsCheckbox = document.querySelector(
          ".terms-checkbox-container"
        );
        if (termsCheckbox) {
          termsCheckbox.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return;
      }
    }

    setStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };
  const prev = () => setStep((s) => Math.max(s - 1, 1));

  // Combined Save & Continue function
  const saveAndContinue = async () => {
    if (!validateCurrentStep(step)) {
      return;
    }

    // Save the registration and get success status
    const saveSuccess = await saveStep();

    // If save was successful, sync registration state from backend before proceeding
    if (saveSuccess) {
      try {
        // Use latest email and conferenceName for fetch
        const emailToFetch = formData.email;
        const confNameToFetch = formData.conference || conferenceName;
        if (emailToFetch && confNameToFetch) {
          // Fetch latest registration from backend
          const fetchUrl = `${REGISTRATION_BASE_URL}/registration/user/${encodeURIComponent(
            emailToFetch
          )}/${encodeURIComponent(confNameToFetch)}`;
          const dataResponse = await fetch(fetchUrl);
          if (dataResponse.ok) {
            const registrations = await dataResponse.json();
            // Find the registration for this specific conference
            const existingReg = registrations.find(
              (reg) => reg.conference === confNameToFetch && !reg.cancelled
            );
            if (existingReg) {
              setFormData((prev) => ({ ...prev, ...existingReg }));

              // CRITICAL: Don't overwrite accompanyingPersons state here!
              // The saveStep() function already updated it properly after reloading.
              // If we overwrite here, we'll restore deleted items from the database.
              // The state should remain as the user set it, and saveStep() already synced it.

              // Only sync focused sessions (not accompanying persons)
              const storedAdditionalFees =
                existingReg.additionalFees &&
                Array.isArray(existingReg.additionalFees)
                  ? existingReg.additionalFees
                  : [];
              const storedZeroPayments =
                existingReg.zeroPayments &&
                Array.isArray(existingReg.zeroPayments)
                  ? existingReg.zeroPayments
                  : [];

              // Focused sessions: build array of objects with paid/paymentId
              if (accompanyingPersonFee) {
                const focusedSessionsFromAdditionalFees = storedAdditionalFees
                  .filter((fee) => fee.feeTypeId !== accompanyingPersonFee.id)
                  .map((fee) => fee.feeTypeId); // Just the ID, not the whole object
                const focusedSessionsFromZeroPayments = storedZeroPayments
                  .filter((zp) => zp.feeTypeId !== accompanyingPersonFee.id)
                  .map((zp) => zp.feeTypeId); // Just the ID, not the whole object
                // Combine and deduplicate
                const focusedSessionsList = [
                  ...new Set([
                    ...focusedSessionsFromAdditionalFees,
                    ...focusedSessionsFromZeroPayments,
                  ]),
                ];
                setRegisterForFocusedSessions(
                  focusedSessionsList.length > 0 ? "Yes" : "No"
                );
                setSelectedFocusedSessions(focusedSessionsList);
                // Optionally, fetch fees for these sessions if needed
              }
            }
          }
        }
      } catch (err) {
        // If sync fails, proceed anyway
        console.error("Failed to sync registration after save:", err);
      }
      setStep((s) => Math.min(s + 1, TOTAL_STEPS));
    }
  };

  // Save current step
  const saveStep = async () => {
    // For partial save, validate basic fields but be flexible with mobile if no country selected
    const firstNameValid = validateField("firstName", formData.firstName);
    const lastNameValid = validateField("lastName", formData.lastName);
    const emailValid = validateField("email", formData.email);

    // For mobile validation during save, use generic validation if no country selected
    let mobileValid = true;
    if (formData.mobile && formData.mobile.trim()) {
      if (formData.country) {
        // If country is selected, do full validation
        mobileValid = validateField("mobile", formData.mobile, {
          country: formData.country,
          value: formData.mobile,
        });
      } else {
        // If no country selected, do basic mobile validation
        const normalizedMobile = formData.mobile.replace(/[\s()-+]/g, "");
        if (!normalizedMobile) {
          setErrors((prev) => ({
            ...prev,
            mobile: "Mobile number is required",
          }));
          mobileValid = false;
        } else if (
          normalizedMobile.length < 7 ||
          normalizedMobile.length > 15 ||
          !/^\d+$/.test(normalizedMobile)
        ) {
          setErrors((prev) => ({
            ...prev,
            mobile: "Please enter a valid mobile number (7-15 digits)",
          }));
          mobileValid = false;
        } else {
          setErrors((prev) => ({ ...prev, mobile: "" }));
          mobileValid = true;
        }
      }
    } else {
      // Mobile is required
      setErrors((prev) => ({ ...prev, mobile: "Mobile number is required" }));
      mobileValid = false;
    }

    if (!firstNameValid || !lastNameValid || !emailValid || !mobileValid) {
      alert("Please correct the highlighted contact details before saving.");
      setStep(1);
      return false; // Return false to indicate validation failure
    }
    setSaving(true);
    try {
      // Build additionalFees array from NEW accompanying persons and focused sessions only
      const additionalFeesArray = [];

      // Get existing additional fees AND zero payments from formData
      const existingAdditionalFees = formData.additionalFees || [];
      const existingZeroPayments = formData.zeroPayments || [];

      console.log("💾 Checking existing items before save:");
      console.log("  - additionalFees:", existingAdditionalFees);
      console.log("  - zeroPayments:", existingZeroPayments);

      // Add only NEW accompanying persons as additional fees (ones not already in DB)
      console.log("🔍 Checking accompanying persons:");
      console.log("  - accompanyingPersons:", accompanyingPersons);
      console.log(
        "  - accompanyingPersons.length:",
        accompanyingPersons.length
      );
      console.log("  - accompanyingPersonFee:", accompanyingPersonFee);

      if (accompanyingPersonFee) {
        console.log("✅ Conditions met - processing accompanying persons");

        // CRITICAL FIX: Send ALL current accompanying persons (not just new ones)
        // Backend needs the complete list to know what to keep/remove
        accompanyingPersons.forEach((person, index) => {
          console.log(`  - Processing person ${index + 1}:`, person);

          // Only add if name is valid
          if (person.name && person.name.trim() !== "") {
            // Check if already PAID (has payment)
            const alreadyPaid = person.paid && person.paymentId;

            if (alreadyPaid) {
              console.log(
                "  ⏭️ Skipping PAID person (backend will preserve):",
                person.name
              );
              // Don't send paid items - backend preserves them automatically
            } else {
              console.log(
                "  ➕ Adding UNPAID person (will sync to backend):",
                person.name
              );
              additionalFeesArray.push({
                feeTypeId: accompanyingPersonFee.id,
                name: person.name,
                rate: parseFloat(accompanyingPersonFee.rate || 0), // Include rate for backend
              });
            }
          } else {
            console.log("  ⚠️ Skipping person with empty name");
          }
        });

        console.log(
          `  📊 Total unpaid persons sent to backend: ${
            additionalFeesArray.filter(
              (f) => f.feeTypeId === accompanyingPersonFee.id
            ).length
          }`
        );
      } else {
        console.log("⚠️ accompanyingPersonFee is null or undefined");
      }

      // Add only NEW focused sessions as additional fees (ones not already in DB)
      if (selectedFocusedSessions.length > 0) {
        selectedFocusedSessions.forEach((sessionId) => {
          // Skip if this session is already in the database (in additionalFees OR zeroPayments)
          const alreadyInAdditionalFees = existingAdditionalFees.some(
            (fee) => fee.feeTypeId === sessionId
          );

          const alreadyInZeroPayments = existingZeroPayments.some(
            (zp) => zp.feeTypeId === sessionId
          );

          const alreadyExists =
            alreadyInAdditionalFees || alreadyInZeroPayments;

          if (!alreadyExists) {
            const session = focusedSessions.find((s) => s.id === sessionId);
            const sessionFee = focusedSessionFees[sessionId];
            if (session && sessionFee) {
              console.log(
                "  ➕ Adding NEW focused session:",
                session.value || session.name,
                "rate:",
                sessionFee.rate
              );
              additionalFeesArray.push({
                feeTypeId: sessionId, // Use sessionId as the feeTypeId (not sessionFee.id!)
                name:
                  session.value ||
                  session.name ||
                  `Focused Session ${sessionId}`,
                rate: parseFloat(sessionFee.rate || 0), // Include rate so backend knows if it's free
              });
            }
          } else {
            const session = focusedSessions.find((s) => s.id === sessionId);
            console.log(
              "  ⏭️ Skipping existing focused session:",
              session?.value || session?.name || sessionId
            );
          }
        });
      }

      console.log("🆕 New additional fees to be added:", additionalFeesArray);
      console.log("📋 Existing additional fees:", existingAdditionalFees);
      console.log("🆓 Existing zero payments:", existingZeroPayments);

      console.log("═══════════════════════════════════════════");
      console.log("🎯 FINAL CHECK BEFORE SENDING TO BACKEND:");
      console.log(
        "  - additionalFeesArray.length:",
        additionalFeesArray.length
      );
      console.log(
        "  - additionalFeesArray:",
        JSON.stringify(additionalFeesArray, null, 2)
      );
      console.log("═══════════════════════════════════════════");

      // Map to new v2 API format with user profile data for new user creation
      const conferenceRegistrationData = {
        // User identification
        email: formData.email,

        // User profile data (for new user creation if needed)
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.mobile,
        gender: formData.gender,
        country: formData.country,
        state: formData.state,
        city: formData.city,
        address: formData.address,
        zipCode: formData.pincode,

        // Conference details
        conference: formData.conference || conferenceName || currentHost,
        affiliation: formData.affiliation || "",
        institution: formData.institution || "",
        prefix: formData.prefix || "",
        fullNameCertificate: formData.fullNameCertificate,
        mciRegistered: formData.mciRegistered || "No",
        mciName: formData.mciName || "",
        mciNumber: formData.mciNumber || "",
        department: formData.department || formData.institution,
        departmentId: formData.departmentId,
        otherDepartment: formData.otherDepartment,
        category: formData.category || "TBD",
        categoryId: formData.categoryId,
        registrationType: formData.registrationType || "TBD",
        registrationTypeId: formData.registrationTypeId,
        membershipNumber: formData.membershipNumber || "",
        isaMembershipNumber: formData.isaMembershipNumber || "",
        // Don't send base64 data to studentIdURL - it's too large and handled separately
        studentIdURL:
          formData.studentIdCard && !formData.studentIdCard.startsWith("data:")
            ? formData.studentIdCard
            : null,
        specialRequirements: formData.specialRequirements || "",
        dietaryRestrictions: formData.dietaryRestrictions || "",
        accommodationNeeded: formData.accommodationNeeded || false,
        notes: formData.notes || "",
        // NOTE: accompanyingPersons are sent via additionalFees array (backend DTO doesn't have accompanyingPersons field)
      };

      // CRITICAL: Always send additionalFees array (even if empty) to sync deletions
      // Backend will remove any unpaid items NOT in this list
      conferenceRegistrationData.additionalFees = additionalFeesArray;
      console.log("📤 Sending additionalFees to backend (will sync with DB):");
      console.log("   Total items:", additionalFeesArray.length);
      console.log(
        "   Accompanying persons:",
        additionalFeesArray.filter(
          (f) => f.feeTypeId === accompanyingPersonFee?.id
        ).length
      );
      console.log(
        "   Focused sessions:",
        additionalFeesArray.filter(
          (f) => f.feeTypeId !== accompanyingPersonFee?.id
        ).length
      );

      console.log("👥 Items being sent in additionalFees array:");
      conferenceRegistrationData.additionalFees.forEach((fee, idx) => {
        const itemType =
          fee.feeTypeId === accompanyingPersonFee?.id
            ? "Accompanying Person"
            : "Focused Session";
        console.log(`   [${idx}] ${itemType}: ${fee.name} - rate: ${fee.rate}`);
      });
      console.log(
        "   ⚠️ Backend will DELETE any UNPAID items NOT in this list!"
      );

      // Determine if this is an update or new registration
      const isUpdate = formData.id || formData.regId;
      const method = isUpdate ? "PUT" : "POST";
      // CRITICAL: Use formData.id (registrationId like "isa005"), NOT formData.regId (dbId like 8)
      const registrationIdForUpdate = formData.id || formData.regId;
      const url = isUpdate
        ? `${CONFERENCE_REGISTRATION_URL}/${registrationIdForUpdate}`
        : CONFERENCE_REGISTRATION_URL;

      console.log("💾 Save Registration:");
      console.log("  formData.id (registrationId):", formData.id);
      console.log("  formData.regId (dbId):", formData.regId);
      console.log("  Using registrationIdForUpdate:", registrationIdForUpdate);
      console.log("  Method:", method);
      console.log("  URL:", url);
      console.log("  Is Update?:", isUpdate);
      console.log("  Is New Conference Registration?:", !isUpdate);
      console.log("  Conference:", conferenceRegistrationData.conference);
      console.log("  Category:", conferenceRegistrationData.category);
      console.log("  CategoryId:", conferenceRegistrationData.categoryId);
      console.log(
        "  Registration Type:",
        conferenceRegistrationData.registrationType
      );
      console.log(
        "  RegistrationTypeId:",
        conferenceRegistrationData.registrationTypeId
      );
      console.log("═══════════════════════════════════════════");
      console.log("📤 PAYLOAD BEING SENT TO BACKEND:");
      console.log(JSON.stringify(conferenceRegistrationData, null, 2));
      console.log("═══════════════════════════════════════════");

      const res = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(conferenceRegistrationData),
      });

      if (!res.ok) {
        const txt = await res.text();
        console.error("❌ Save failed!");
        console.error("  Status:", res.status, res.statusText);
        console.error("  Response:", txt);
        console.error(
          "  Was trying to save for existing user:",
          !!formData.email
        );
        console.error("  Conference:", conferenceRegistrationData.conference);
        alert(`Save failed: ${txt}`);
        return false; // Return false to indicate save failure
      } else {
        const saved = await res.json();
        console.log("✅ Save successful!");
        console.log("  Saved data:", saved);
        console.log("  Registration ID:", saved.registrationId);
        console.log("  DB ID:", saved.dbId || saved.id);

        // Set flag to indicate registration was just updated successfully
        setRegistrationJustUpdated(true);

        // Update registration IDs
        if (saved.registrationId) {
          console.log("  Setting formData.id to:", saved.registrationId);
          setFormData((prev) => ({ ...prev, id: saved.registrationId }));
        }
        if (saved.dbId || saved.id) {
          console.log("  Setting formData.regId to:", saved.dbId || saved.id);
          setFormData((prev) => ({ ...prev, regId: saved.dbId || saved.id }));
        }

        // Upload student ID card file to database if present
        if (
          (saved.dbId || saved.id) &&
          formData.studentIdCard &&
          formData.studentIdCard.startsWith("data:")
        ) {
          try {
            const registrationId = saved.dbId || saved.id;
            console.log(
              "📤 Uploading student ID card to database for registration:",
              registrationId
            );

            const base64Data = formData.studentIdCard.split(",")[1];
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }

            const fileData = {
              fileType: "id-card",
              fileData: base64Data,
              fileName: formData.studentIdCardName || "student-id.jpg",
              contentType: formData.studentIdCardType || "image/jpeg",
              fileSize: byteNumbers.length,
            };

            console.log("📤 File data prepared:", {
              fileName: fileData.fileName,
              contentType: fileData.contentType,
              fileSize: fileData.fileSize,
              base64Length: base64Data.length,
            });

            const uploadUrl = `${REGISTRATION_BASE_URL}/registration/${registrationId}/file/id-card`;
            console.log("📤 Uploading to:", uploadUrl);

            const fileUploadRes = await fetch(uploadUrl, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(fileData),
            });

            if (!fileUploadRes.ok) {
              const errorText = await fileUploadRes.text();
              console.error("❌ Failed to upload student ID card:", errorText);
              alert("Warning: File upload failed. Please try uploading again.");
              // Don't fail the entire save, just log it
            } else {
              const result = await fileUploadRes.json();
              console.log(
                "✅ Student ID card uploaded successfully to database:",
                result
              );

              // Clear the base64 data and set a marker that file is in database
              setFormData((prev) => ({
                ...prev,
                studentIdCard: "DATABASE", // Marker to indicate file is stored in database
                studentIdCardUploaded: true,
              }));
            }
          } catch (fileErr) {
            console.error("Error uploading student ID card:", fileErr);
            // Don't fail the entire save, just log it
          }
        }

        // Reload full registration data to get updated additionalFees
        if (saved.dbId || saved.id) {
          try {
            const reloadRes = await fetch(
              `${CONFERENCE_REGISTRATION_URL}/${saved.dbId || saved.id}`
            );
            if (reloadRes.ok) {
              const reloadedReg = await reloadRes.json();
              console.log(
                "📥 Reloaded registration data from backend:",
                reloadedReg
              );
              console.log(
                "   Backend returned additionalFees count:",
                reloadedReg.additionalFees?.length || 0
              );
              console.log(
                "   Backend returned zeroPayments count:",
                reloadedReg.zeroPayments?.length || 0
              );

              // Update formData with additionalFees AND zeroPayments so the payment detection works
              if (
                reloadedReg.additionalFees &&
                Array.isArray(reloadedReg.additionalFees)
              ) {
                setFormData((prev) => ({
                  ...prev,
                  additionalFees: reloadedReg.additionalFees,
                  zeroPayments: reloadedReg.zeroPayments || [], // Also update zeroPayments
                }));
                console.log(
                  "✅ Updated formData.additionalFees:",
                  reloadedReg.additionalFees
                );
                console.log(
                  "✅ Updated formData.zeroPayments:",
                  reloadedReg.zeroPayments
                );

                // CRITICAL: Only update accompanyingPersons to ADD database IDs
                // Do NOT restore deleted items from database!
                if (accompanyingPersonFee) {
                  console.log(
                    "🔍 Current accompanyingPersons state (before mapping):",
                    accompanyingPersons
                  );
                  console.log(
                    "   Current state count:",
                    accompanyingPersons.length
                  );

                  const updatedAccompanyingPersons = accompanyingPersons.map(
                    (person) => {
                      // Find matching fee in reloaded data (check both additionalFees and zeroPayments)
                      const matchingFee = reloadedReg.additionalFees.find(
                        (fee) =>
                          fee.feeTypeId === accompanyingPersonFee.id &&
                          fee.name === person.name
                      );

                      const matchingZeroPayment =
                        reloadedReg.zeroPayments?.find(
                          (zp) =>
                            zp.feeTypeId === accompanyingPersonFee.id &&
                            zp.name === person.name
                        );

                      if (matchingFee) {
                        console.log(
                          `  ✓ Found match in additionalFees for "${person.name}":`,
                          matchingFee
                        );
                        // Update with database info from additionalFees
                        return {
                          ...person,
                          paymentId: matchingFee.paymentId || null,
                          paid: !!matchingFee.paymentId,
                          feeTypeId: matchingFee.feeTypeId,
                          id: matchingFee.id, // Add the database ID
                        };
                      } else if (matchingZeroPayment) {
                        console.log(
                          `  ✓ Found match in zeroPayments for "${person.name}":`,
                          matchingZeroPayment
                        );
                        // Update with database info from zeroPayments
                        return {
                          ...person,
                          paymentId: null,
                          paid: true, // Saved in zero_payments table
                          feeTypeId: matchingZeroPayment.feeTypeId,
                          id: matchingZeroPayment.id,
                        };
                      }
                      console.log(
                        `  ⚠️ No match found for "${person.name}" - keeping as is (new item)`
                      );
                      return person;
                    }
                  );

                  setAccompanyingPersons(updatedAccompanyingPersons);
                  console.log(
                    "✅ Updated accompanyingPersons state after save (with DB IDs):",
                    updatedAccompanyingPersons
                  );
                  console.log(
                    "   Updated state count:",
                    updatedAccompanyingPersons.length
                  );
                  console.log(
                    "   If count decreased, deletions were preserved ✓"
                  );
                  console.log(
                    "   If count stayed same/increased, items may have been restored from DB ✗"
                  );
                }
              }
            }
          } catch (reloadErr) {
            console.error("Error reloading registration:", reloadErr);
            // Don't fail the save operation, just log it
          }
        }

        return true; // Return true to indicate successful save
      }
    } catch (err) {
      console.error("Save error:", err);
      alert(`Could not save. Error: ${err.message}`);
      return false; // Return false to indicate error
    } finally {
      setSaving(false);
    }
  };

  // Detect environment based on Razorpay key
  const isProduction =
    RAZORPAY_KEY_ID && RAZORPAY_KEY_ID.startsWith("rzp_live_");

  // Payment
  const startPayment = async () => {
    // Set payment initiated flag to prevent double clicks
    setPaymentInitiated(true);
    // Reset payment error flags for new payment attempt
    setPaymentErrorShown(false);
    paymentFailureHandledRef.current = false;
    razorpayOpenRef.current = false;

    const missingFields = [];
    if (!formData.firstName.trim()) missingFields.push("First Name");
    if (!formData.lastName.trim()) missingFields.push("Last Name");
    if (!formData.email.trim()) missingFields.push("Email");
    if (!formData.mobile.trim()) missingFields.push("Mobile");
    if (!formData.country.trim()) missingFields.push("Country");

    if (missingFields.length > 0) {
      alert(
        `Please fill in the following required fields: ${missingFields.join(
          ", "
        )}`
      );
      setStep(1);
      setPaymentInitiated(false); // Reset flag on validation error
      return;
    }

    if (!validateAllMandatoryFields()) {
      alert("Please correct the highlighted contact details.");
      setStep(1);
      setPaymentInitiated(false); // Reset flag on validation error
      return;
    }

    setProcessingPayment(true);

    // MANDATORY: Ensure registration is saved successfully before proceeding to payment
    let registrationId = formData.id;
    let registrationDbId = formData.regId;

    console.log("💳 Starting payment process");
    console.log("  formData.id:", formData.id);
    console.log("  formData.regId:", formData.regId);
    console.log("  registrationId:", registrationId);
    console.log("  registrationDbId:", registrationDbId);

    if (!registrationId) {
      console.log(
        "⚠️ No registrationId found - need to save registration first"
      );

      try {
        // Map to new v2 API format with user profile data for new user creation
        const conferenceRegistrationData = {
          // User identification
          email: formData.email,

          // User profile data (for new user creation if needed)
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.mobile,
          country: formData.country,
          state: formData.state,
          city: formData.city,
          address: formData.address,
          zipCode: formData.pincode,
          institution: formData.institution,
          affiliation: formData.affiliation || "",
          // Conference details
          conference: formData.conference || conferenceName || currentHost,
          prefix: formData.prefix || "",
          fullNameCertificate: formData.fullNameCertificate,
          mciRegistered: formData.mciRegistered || "No",
          mciName: formData.mciName || "",
          mciNumber: formData.mciNumber || "",
          department: formData.department || formData.institution,
          departmentId: formData.departmentId,
          otherDepartment: formData.otherDepartment,
          category: formData.category || "TBD",
          categoryId: formData.categoryId,
          registrationType: formData.registrationType || "TBD",
          registrationTypeId: formData.registrationTypeId,
          membershipNumber: formData.membershipNumber || "",
          isaMembershipNumber: formData.isaMembershipNumber || "",
          // Don't send base64 data to studentIdURL - it's too large and handled separately
          studentIdURL:
            formData.studentIdCard &&
            !formData.studentIdCard.startsWith("data:")
              ? formData.studentIdCard
              : null,
          specialRequirements: formData.specialRequirements || "",
          dietaryRestrictions: formData.dietaryRestrictions || "",
          accommodationNeeded: formData.accommodationNeeded || false,
          notes: formData.notes || "",
          // Accompanying persons
          accompanyingPersons:
            accompanyingPersons.length > 0 ? accompanyingPersons : [],
        };

        // CRITICAL FIX: We're inside !registrationId block, meaning formData.id is NULL
        // This means we should ALWAYS do POST for new registration
        // DO NOT use formData.regId here as it's the database ID, not the user's registration ID
        const paymentMethod = "POST";
        const paymentUrl = CONFERENCE_REGISTRATION_URL;

        console.log("🔄 Payment flow registration save (NEW registration):");
        console.log(
          "  formData.id (registrationId):",
          formData.id,
          "← NULL, so doing POST"
        );
        console.log(
          "  formData.regId (dbId):",
          formData.regId,
          "← IGNORED in this flow"
        );
        console.log("  Method:", paymentMethod);
        console.log("  URL:", paymentUrl);

        const regSaveRes = await fetch(paymentUrl, {
          method: paymentMethod,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(conferenceRegistrationData),
        });

        if (!regSaveRes.ok) {
          const regError = await regSaveRes.text();
          console.error("Registration save failed:", regError);
          alert(
            `Registration failed: ${regError}. Please fix the issues and try again.`
          );
          setProcessingPayment(false);
          setPaymentInitiated(false); // Reset flag on registration error
          setStep(1); // Go back to first step to fix issues
          return;
        }

        const savedReg = await regSaveRes.json();
        registrationId = savedReg.registrationId;
        registrationDbId = savedReg.dbId || savedReg.id;

        console.log("💾 Registration saved response:");
        console.log("  savedReg.registrationId:", savedReg.registrationId);
        console.log("  savedReg.dbId:", savedReg.dbId);
        console.log("  savedReg.id:", savedReg.id);
        console.log("  Using registrationId:", registrationId);
        console.log("  Using registrationDbId:", registrationDbId);

        // Update form data with the new ID
        setFormData((prev) => ({
          ...prev,
          id: savedReg.registrationId,
          regId: savedReg.dbId || savedReg.id,
        }));

        const paymentActionMessage = isPaymentUpdate ? "updated" : "saved";
        alert(
          `Registration ${paymentActionMessage} successfully! Registration ID: ${registrationId}. Proceeding to payment...`
        );
      } catch (regError) {
        console.error("Error saving registration before payment:", regError);
        alert(`Registration failed: ${regError.message}. Please try again.`);
        setProcessingPayment(false);
        setPaymentInitiated(false); // Reset flag on registration error
        setStep(1); // Go back to first step to fix issues
        return;
      }
    } else {
      console.log("✅ Registration already exists");
      console.log("  registrationId:", registrationId);
      console.log("  registrationDbId:", registrationDbId);
    }

    // Determine currency and amount based on fetched fee or fallback to country-based defaults
    let currency, amountInSmallestUnit, actualAmount;

    console.log("💵 Starting fee calculation:");
    console.log("  formData.paid:", formData.paid);
    console.log("  registrationFee:", registrationFee);
    console.log("  accompanyingPersons.length:", accompanyingPersons.length);
    console.log(
      "  selectedFocusedSessions.length:",
      selectedFocusedSessions.length
    );

    if (registrationFee && registrationFee.rate) {
      // Use the fetched fee from the backend
      currency = registrationFee.currency || "INR";

      // Check if this is an additional payment (already paid base fee)
      const isAdditionalPayment = formData.paid === true;
      console.log("  isAdditionalPayment:", isAdditionalPayment);

      if (isAdditionalPayment) {
        // Only charge for NEW items, not the base fee
        actualAmount = 0;

        // CRITICAL: Calculate existing PAID items from additionalFees
        // MUST filter to only items with paymentId (already paid)
        // Items with paymentId=null are NEW items that need payment
        const existingAdditionalFees = formData.additionalFees || [];

        console.log("  📊 Analyzing existing additional fees:");
        console.log(
          "    Total additionalFees count:",
          existingAdditionalFees.length
        );
        existingAdditionalFees.forEach((fee) => {
          console.log(
            `    - Fee: ${fee.name}, feeTypeId: ${fee.feeTypeId}, paymentId: ${
              fee.paymentId || "NULL"
            }`
          );
        });

        // Count ONLY paid accompanying persons (those with paymentId)
        const existingAccompanyingCount = existingAdditionalFees.filter(
          (fee) =>
            accompanyingPersonFee &&
            fee.feeTypeId === accompanyingPersonFee.id &&
            fee.paymentId
        ).length;

        console.log(
          "    Existing PAID accompanying count:",
          existingAccompanyingCount
        );

        // Get ONLY paid focused session IDs (those with paymentId)
        const existingFocusedSessionIds = existingAdditionalFees
          .filter((fee) => {
            // If no accompanyingPersonFee loaded, treat all fees as focused sessions
            if (!accompanyingPersonFee) return fee.paymentId; // Only if paid
            // Otherwise, exclude accompanying person fees and only include paid items
            return fee.feeTypeId !== accompanyingPersonFee.id && fee.paymentId;
          })
          .map((fee) => fee.feeTypeId);

        console.log(
          "    Existing PAID focused session IDs:",
          existingFocusedSessionIds
        );

        // Add only NEW accompanying persons fees
        if (
          accompanyingPersons.length > existingAccompanyingCount &&
          accompanyingPersonFee &&
          accompanyingPersonFee.rate
        ) {
          const newAccompanyingCount =
            accompanyingPersons.length - existingAccompanyingCount;
          const accompanyingTotal =
            parseFloat(accompanyingPersonFee.rate) * newAccompanyingCount;
          actualAmount += accompanyingTotal;
        }

        // Add only NEW focused session fees (excluding free ones with rate = 0)
        if (selectedFocusedSessions.length > 0) {
          selectedFocusedSessions.forEach((sessionId) => {
            if (!existingFocusedSessionIds.includes(sessionId)) {
              const sessionFee = focusedSessionFees[sessionId];
              // Only add to amount if rate > 0 (exclude free items)
              if (
                sessionFee &&
                sessionFee.rate &&
                parseFloat(sessionFee.rate) > 0
              ) {
                actualAmount += parseFloat(sessionFee.rate);
              }
            }
          });
        }
      } else {
        // Initial payment - include base fee and all additional items
        actualAmount = parseFloat(registrationFee.rate);

        // Add accompanying persons fees if applicable
        if (
          accompanyingPersons.length > 0 &&
          accompanyingPersonFee &&
          accompanyingPersonFee.rate
        ) {
          const accompanyingTotal =
            parseFloat(accompanyingPersonFee.rate) * accompanyingPersons.length;
          actualAmount += accompanyingTotal;
        }

        // Add focused session fees (excluding free ones with rate = 0)
        if (selectedFocusedSessions.length > 0) {
          selectedFocusedSessions.forEach((sessionId) => {
            const sessionFee = focusedSessionFees[sessionId];
            // Only add to amount if rate > 0 (exclude free items)
            if (
              sessionFee &&
              sessionFee.rate &&
              parseFloat(sessionFee.rate) > 0
            ) {
              actualAmount += parseFloat(sessionFee.rate);
            }
          });
        }
      }

      // Add 3.54% convenience fee to actual amount
      const convenienceFeeRate = 0.0354;
      const convenienceFee = actualAmount * convenienceFeeRate;
      const totalAmountWithFee = actualAmount + convenienceFee;

      // Convert to smallest unit (paise for INR, cents for USD)
      amountInSmallestUnit = Math.round(totalAmountWithFee * 100);

      console.log("💰 Fee Calculation Results:");
      console.log("  registrationFee:", registrationFee);
      console.log("  actualAmount (before convenience fee):", actualAmount);
      console.log("  convenienceFee (3.54%):", convenienceFee);
      console.log("  totalAmountWithFee:", totalAmountWithFee);
      console.log("  amountInSmallestUnit:", amountInSmallestUnit);
      console.log("  currency:", currency);
    } else {
      // Fallback to country-based defaults
      currency = formData.country === "India" ? "INR" : "USD";
      amountInSmallestUnit = formData.country === "India" ? 10000 : 100; // 100 INR or 1 USD
      actualAmount = amountInSmallestUnit / 100;

      console.log("💰 Fee Calculation (FALLBACK):");
      console.log("  No registrationFee found, using fallback");
      console.log("  actualAmount:", actualAmount);
      console.log("  amountInSmallestUnit:", amountInSmallestUnit);
      console.log("  currency:", currency);
    }

    try {
      // Build breakdown array for backend
      const breakdown = [];

      console.log("🔨 Building payment breakdown array...");
      console.log("  formData.paid:", formData.paid);
      console.log("  accompanyingPersons:", accompanyingPersons);
      console.log("  selectedFocusedSessions:", selectedFocusedSessions);

      // Check if this is an additional payment
      const isAdditionalPayment = formData.paid === true;
      console.log("  isAdditionalPayment:", isAdditionalPayment);

      if (isAdditionalPayment) {
        // Only include NEW items in breakdown
        // CRITICAL: Count only PAID items (those with paymentId)
        // Items with paymentId=null are NEW and should be included in breakdown
        const existingAdditionalFees = formData.additionalFees || [];

        console.log("  📊 Building breakdown for additional payment:");
        console.log("    Total additionalFees:", existingAdditionalFees.length);

        // Count ONLY paid accompanying persons (those with paymentId)
        const existingAccompanyingCount = existingAdditionalFees.filter(
          (fee) =>
            accompanyingPersonFee &&
            fee.feeTypeId === accompanyingPersonFee.id &&
            fee.paymentId
        ).length;

        console.log(
          "    Existing PAID accompanying count:",
          existingAccompanyingCount
        );
        console.log(
          "    Current accompanying persons:",
          accompanyingPersons.length
        );

        // Get ONLY paid focused session IDs (those with paymentId)
        const existingFocusedSessionIds = existingAdditionalFees
          .filter((fee) => {
            // If no accompanyingPersonFee loaded, treat all fees as focused sessions
            if (!accompanyingPersonFee) return fee.paymentId; // Only if paid
            // Otherwise, exclude accompanying person fees and only include paid items
            return fee.feeTypeId !== accompanyingPersonFee.id && fee.paymentId;
          })
          .map((fee) => fee.feeTypeId);

        console.log(
          "    Existing PAID focused session IDs:",
          existingFocusedSessionIds
        );
        console.log(
          "    Currently selected focused sessions:",
          selectedFocusedSessions
        );

        // Add only NEW accompanying persons
        if (
          accompanyingPersons.length > existingAccompanyingCount &&
          accompanyingPersonFee &&
          accompanyingPersonFee.rate
        ) {
          const newPersons = accompanyingPersons.slice(
            existingAccompanyingCount
          );
          newPersons.forEach((person, index) => {
            breakdown.push({
              type: "ADDITIONAL_FEE",
              feeId: accompanyingPersonFee.id || null,
              amount: parseFloat(accompanyingPersonFee.rate),
              currency: accompanyingPersonFee.currency || currency,
              description: `Accompanying Person - ${
                person.name || `Person ${existingAccompanyingCount + index + 1}`
              }`,
            });
          });
        }

        // Add only NEW focused sessions (exclude free items with rate = 0)
        if (selectedFocusedSessions.length > 0) {
          selectedFocusedSessions.forEach((sessionId) => {
            if (!existingFocusedSessionIds.includes(sessionId)) {
              const sessionFee = focusedSessionFees[sessionId];
              const session = focusedSessions.find((s) => s.id === sessionId);

              // Only add to breakdown if rate > 0 (exclude free items)
              if (
                sessionFee &&
                session &&
                sessionFee.rate &&
                parseFloat(sessionFee.rate) > 0
              ) {
                breakdown.push({
                  type: "ADDITIONAL_FEE",
                  feeId: sessionId, // Use sessionId as the feeTypeId (not sessionFee.id!)
                  amount: parseFloat(sessionFee.rate),
                  currency: sessionFee.currency || currency,
                  description: `Focused Session - ${
                    session.value || session.name || `Session ${sessionId}`
                  }`,
                });
              }
            }
          });
        }
      } else {
        // Initial payment - include base fee and all items
        console.log(
          "  📝 Building breakdown for INITIAL payment (includes base fee)"
        );

        // Get existing additional fees to check for duplicates
        const existingAdditionalFees = formData.additionalFees || [];

        // Add base registration fee
        if (registrationFee && registrationFee.rate) {
          console.log("    ✅ Adding BASE_FEE to breakdown");
          console.log("       feeId:", registrationFee.id);
          console.log("       amount:", parseFloat(registrationFee.rate));
          breakdown.push({
            type: "BASE_FEE",
            feeId: registrationFee.id || null,
            amount: parseFloat(registrationFee.rate),
            currency: registrationFee.currency || currency,
            description: `Registration Fee - ${formData.category} (${formData.registrationType})`,
          });
        } else {
          console.warn(
            "    ⚠️ Cannot add BASE_FEE - registrationFee or rate is missing"
          );
          console.warn("       registrationFee:", registrationFee);
        }

        // Add accompanying persons fees (ONLY unpaid ones for additional payment)
        console.log("    Checking accompanying persons...");
        console.log(
          "      accompanyingPersons.length:",
          accompanyingPersons.length
        );
        console.log("      accompanyingPersonFee:", accompanyingPersonFee);
        if (
          accompanyingPersons.length > 0 &&
          accompanyingPersonFee &&
          accompanyingPersonFee.rate
        ) {
          // For INITIAL payment: Include ALL persons who don't have a paymentId
          // (Don't filter by alreadyInDb - that's only for additional payments)
          const unpaidPersons = accompanyingPersons.filter((person, index) => {
            // Only exclude if already paid (has paymentId)
            const alreadyPaid = person.paid || person.paymentId;
            return !alreadyPaid;
          });

          console.log("      unpaidPersons.length:", unpaidPersons.length);
          unpaidPersons.forEach((person, index) => {
            console.log(
              `      ✅ Adding accompanying person ${index + 1}: ${person.name}`
            );
            breakdown.push({
              type: "ADDITIONAL_FEE",
              feeId: accompanyingPersonFee.id || null,
              amount: parseFloat(accompanyingPersonFee.rate),
              currency: accompanyingPersonFee.currency || currency,
              description: `Accompanying Person - ${
                person.name || `Person ${index + 1}`
              }`,
            });
          });
        } else {
          console.log(
            "      ⏭️  Skipping accompanying persons (none added or fee not loaded)"
          );
        }

        // Add focused session fees (exclude free items with rate = 0)
        console.log("    Checking focused sessions...");
        console.log(
          "      selectedFocusedSessions.length:",
          selectedFocusedSessions.length
        );
        if (selectedFocusedSessions.length > 0) {
          selectedFocusedSessions.forEach((sessionId) => {
            const sessionFee = focusedSessionFees[sessionId];
            const session = focusedSessions.find((s) => s.id === sessionId);

            // Only add to breakdown if rate > 0 (exclude free items)
            if (
              sessionFee &&
              session &&
              sessionFee.rate &&
              parseFloat(sessionFee.rate) > 0
            ) {
              console.log(
                `      ✅ Adding focused session: ${
                  session.value || session.name
                }`
              );
              breakdown.push({
                type: "ADDITIONAL_FEE",
                feeId: sessionId, // Use sessionId as the feeTypeId (not sessionFee.id!)
                amount: parseFloat(sessionFee.rate),
                currency: sessionFee.currency || currency,
                description: `Focused Session - ${
                  session.value || session.name || `Session ${sessionId}`
                }`,
              });
            }
          });
        } else {
          console.log("      ⏭️  No focused sessions selected");
        }
      }

      // Add convenience fee as a separate item in the breakdown
      // Calculate convenience fee based on the sum of all items in breakdown
      if (breakdown.length > 0) {
        const subtotal = breakdown.reduce(
          (sum, item) => sum + parseFloat(item.amount),
          0
        );
        const convenienceFeeForBreakdown = subtotal * 0.0354; // 3.54% of subtotal

        breakdown.push({
          type: "CONVENIENCE_FEE",
          feeId: null, // No specific fee ID for convenience fee
          amount: parseFloat(convenienceFeeForBreakdown.toFixed(2)),
          currency: currency,
          description: "Convenience Fee (3.54%)",
        });

        console.log("💳 Added convenience fee to breakdown:");
        console.log("  Subtotal:", subtotal);
        console.log("  Convenience fee (3.54%):", convenienceFeeForBreakdown);
        console.log("  Total breakdown items now:", breakdown.length);
      }

      // Special handling for zero-amount orders (free items or already paid)
      if (amountInSmallestUnit === 0 || actualAmount === 0) {
        console.log("🆓 Zero amount detected - no payment needed");
        console.log("  This can happen when:");
        console.log(
          "    - User already paid base fee (formData.paid === true)"
        );
        console.log(
          "    - No new accompanying persons or focused sessions added"
        );
        console.log("    - All selected items are free (rate = 0)");

        // For zero-amount orders, we don't need to process payment
        // User should just proceed without payment or save as zero-payment record
        alert(
          "✅ No payment required. All items are either already paid or free."
        );
        setProcessingPayment(false);
        setPaymentInitiated(false);
        return;
      }

      console.log("📋 Payment Breakdown Summary:");
      console.log("  Total items in breakdown:", breakdown.length);
      console.log("  Breakdown details:", JSON.stringify(breakdown, null, 2));
      console.log("  Total amount:", actualAmount, currency);
      console.log("  Amount in smallest unit:", amountInSmallestUnit);

      // Detailed breakdown validation
      if (breakdown.length === 0) {
        console.error("❌ WARNING: Breakdown array is EMPTY!");
        console.error(
          "  This means no items will be saved to PaymentBreakdownTempEntity table"
        );
        console.error("  Check why breakdown items are not being added");
      } else {
        console.log("✅ Breakdown validation:");
        breakdown.forEach((item, index) => {
          console.log(`  [${index}] ${item.type}: ${item.description}`);
          console.log(
            `      feeId: ${item.feeId}, amount: ${item.amount} ${item.currency}`
          );
        });
      }

      // Prepare order request payload
      // IMPORTANT: Use registrationId (from user table), NOT registrationDbId (from registration table)
      const orderRequestPayload = {
        amountInPaise: amountInSmallestUnit,
        currency: currency,
        registrationId: registrationId, // Use registrationId, not registrationDbId
        breakdown: breakdown,
      };

      console.log("🚀 Creating order at endpoint:", ORDER_URL);
      console.log(
        "📤 Order Request Payload:",
        JSON.stringify(orderRequestPayload, null, 2)
      );
      console.log("  - registrationId (user table ID):", registrationId);
      console.log(
        "  - registrationDbId (registration table ID):",
        registrationDbId
      );
      console.log("  - amountInPaise:", amountInSmallestUnit);
      console.log("  - currency:", currency);
      console.log("  - breakdown.length:", breakdown.length);

      const orderResp = await fetch(ORDER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderRequestPayload),
      });

      console.log(
        "📥 Order Response Status:",
        orderResp.status,
        orderResp.statusText
      );

      if (!orderResp.ok) {
        const errorText = await orderResp.text();
        console.error("Order creation failed:", errorText);
        alert(`Unable to create payment order: ${errorText}`);
        setProcessingPayment(false);
        setPaymentInitiated(false); // Reset flag on order creation error
        return;
      }

      const order = await orderResp.json();

      console.log("✅ Order created successfully");
      console.log("📦 Order Response:", JSON.stringify(order, null, 2));
      console.log("🔑 Order identifiers:");
      console.log("  order.id (backend DB):", order.id);
      console.log("  order.orderId (Razorpay):", order.orderId);
      console.log(
        "📊 Breakdown should now be in PaymentBreakdownTempEntity table with orderId:",
        order.id
      );

      // Validate required data first
      if (!RAZORPAY_KEY_ID) {
        throw new Error(
          "Razorpay key is not configured. Please check your .env file."
        );
      }

      // Check if order response is valid
      if (!order || typeof order !== "object") {
        throw new Error("Invalid order response format");
      }

      // Check for the specific error from Java controller
      if (
        order.mapType === "java.util.HashMap" &&
        order.empty === false &&
        !order.id
      ) {
        throw new Error(
          "Backend configuration error: The payment controller is not returning order data properly. Please check Razorpay keys in backend configuration."
        );
      }

      // The PaymentController returns a JSONObject, check for common properties
      if (!order.id && !order.orderId) {
        console.error("Order response missing ID:", order);
        throw new Error(
          "Invalid order response - missing order ID. Response: " +
            JSON.stringify(order)
        );
      }

      await loadRazorpayScript();

      // Build Razorpay options based on environment
      const options = {
        key: RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency || currency,
        name: "Conference Registration",
        description: "Event registration fee",
        order_id: order.id || order.orderId,
        notes: {
          registrationId: registrationDbId || formData.regId || "",
        },
        ...(isProduction
          ? {
              // PRODUCTION: Webhook handles payment, handler just shows success UI
              handler: async function (response) {
                console.log(
                  "🏭 PRODUCTION: Payment completed, webhook will process"
                );

                // Just show success messages and redirect
                // Webhook already processed the payment
                alert("Payment successful! Processing registration...");
                setTimeout(() => {
                  alert("Registered successfully. Details Emailed");
                  // Redirect to login
                  window.location.href = "/";
                }, 500);
              },
            }
          : {
              // LOWER ENV: Use handler (current behavior)
              handler: async function (response) {
                try {
                  // Clean up Razorpay instance on success
                  if (razorpayInstanceRef.current) {
                    razorpayInstanceRef.current = null;
                  }
                  razorpayOpenRef.current = false; // Reset popup flag

                  // Basic validation - ensure we have all required fields
                  if (
                    !response.razorpay_payment_id ||
                    !response.razorpay_order_id ||
                    !response.razorpay_signature
                  ) {
                    throw new Error(
                      "Incomplete payment response from Razorpay"
                    );
                  }

                  // Step 1: Ensure registration is saved first to get an ID
                  let registrationId = formData.id;
                  let registrationDbId = formData.regId;

                  if (!registrationId) {
                    try {
                      // Map to new v2 API format with user profile data for new user creation
                      const conferenceRegistrationData = {
                        // User identification
                        email: formData.email,

                        // User profile data (for new user creation if needed)
                        firstName: formData.firstName,
                        lastName: formData.lastName,
                        phone: formData.mobile,
                        country: formData.country,
                        state: formData.state,
                        city: formData.city,
                        address: formData.address,
                        zipCode: formData.pincode,
                        affiliation: formData.affiliation,
                        institution: formData.institution,
                        // Conference details
                        conference:
                          formData.conference || conferenceName || currentHost,
                        prefix: formData.prefix || "",
                        fullNameCertificate: formData.fullNameCertificate,
                        mciRegistered: formData.mciRegistered || "No",
                        mciName: formData.mciName || "",
                        mciNumber: formData.mciNumber || "",
                        department: formData.department || formData.institution,
                        departmentId: formData.departmentId,
                        otherDepartment: formData.otherDepartment,
                        category: formData.category || "TBD",
                        categoryId: formData.categoryId,
                        registrationType: formData.registrationType || "TBD",
                        registrationTypeId: formData.registrationTypeId,
                        membershipNumber: formData.membershipNumber || "",
                        // Don't send base64 data to studentIdURL - it's too large and handled separately
                        studentIdURL:
                          formData.studentIdCard &&
                          !formData.studentIdCard.startsWith("data:")
                            ? formData.studentIdCard
                            : null,
                        specialRequirements: formData.specialRequirements || "",
                        dietaryRestrictions: formData.dietaryRestrictions || "",
                        accommodationNeeded:
                          formData.accommodationNeeded || false,
                        notes: formData.notes || "",
                      };

                      // Determine if this is an update or new registration in payment handler
                      const isHandlerUpdate = formData.id || formData.regId;
                      const handlerMethod = isHandlerUpdate ? "PUT" : "POST";
                      // Use regId if available, otherwise use id as fallback
                      const handlerRegistrationId =
                        formData.regId || formData.id;
                      const handlerUrl = isHandlerUpdate
                        ? `${CONFERENCE_REGISTRATION_URL}/${handlerRegistrationId}`
                        : CONFERENCE_REGISTRATION_URL;

                      const regSaveRes = await fetch(handlerUrl, {
                        method: handlerMethod,
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(conferenceRegistrationData),
                      });

                      if (regSaveRes.ok) {
                        const savedReg = await regSaveRes.json();
                        registrationId = savedReg.registrationId;
                        registrationDbId = savedReg.dbId || savedReg.id;

                        // Update form data with the new ID
                        setFormData((prev) => ({
                          ...prev,
                          id: savedReg.registrationId,
                          regId: savedReg.dbId || savedReg.id,
                        }));
                      } else {
                        const regError = await regSaveRes.text();
                        console.error("Failed to save registration:", regError);
                        throw new Error(
                          `Failed to save registration: ${regError}`
                        );
                      }
                    } catch (regError) {
                      console.error(
                        "Error saving registration before payment:",
                        regError
                      );
                      throw new Error(
                        `Could not save registration before payment: ${regError.message}`
                      );
                    }
                  }

                  // Step 2: Save payment entity with registration ID (Lower env only - production uses callback_url)
                  try {
                    console.log(
                      "💳 Saving payment (backend will read breakdown from temp table)"
                    );
                    console.log("� Payment identifiers:");
                    console.log(
                      "  orderId (for temp table lookup):",
                      order.id || order.orderId
                    );
                    console.log(
                      "  razorpayOrderId:",
                      response.razorpay_order_id
                    );
                    console.log(
                      "  razorpayPaymentId:",
                      response.razorpay_payment_id
                    );
                    console.log("  registrationDbId:", registrationDbId);

                    // Validate critical fields before sending
                    const orderIdForSave =
                      order.id || order.orderId || response.razorpay_order_id;
                    if (!orderIdForSave) {
                      console.error("❌ CRITICAL: orderId is null/undefined!");
                      console.error("  order:", JSON.stringify(order, null, 2));
                      console.error(
                        "  response:",
                        JSON.stringify(response, null, 2)
                      );
                      throw new Error(
                        "Cannot save payment: orderId is missing. Backend needs this to lookup breakdown from temp table."
                      );
                    }

                    if (!registrationDbId) {
                      console.error(
                        "❌ CRITICAL: registrationDbId is null/undefined!"
                      );
                      console.error("  formData.regId:", formData.regId);
                      console.error("  formData.id:", formData.id);
                      throw new Error(
                        "Cannot save payment: registrationDbId is missing."
                      );
                    }

                    const paymentSaveData = {
                      regId: registrationDbId,
                      orderId: orderIdForSave, // ✅ Backend uses this to lookup temp table
                      razorpayOrderId: response.razorpay_order_id,
                      razorpayPaymentId: response.razorpay_payment_id,
                      razorpaySignature: response.razorpay_signature,
                      status: "SUCCESS",
                      amountPaid: amountInSmallestUnit / 100, // ✅ Uses totalAmountWithFee (includes 3.54% convenience fee)
                      currency: order.currency || currency,
                      // ❌ NOT SENDING BREAKDOWN - Backend reads from temp table using orderId
                    };

                    console.log(
                      "📤 Payment save payload:",
                      JSON.stringify(paymentSaveData, null, 2)
                    );
                    console.log(
                      "✅ Payload validation passed - all critical fields present"
                    );
                    console.log("💰 Amount breakdown:");
                    console.log(
                      "  amountInSmallestUnit (with fee):",
                      amountInSmallestUnit
                    );
                    console.log(
                      "  amountPaid (in main currency):",
                      paymentSaveData.amountPaid
                    );
                    console.log("  This includes 3.54% convenience fee");

                    const paymentSaveRes = await fetch(PAYMENT_SAVE_URL, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify(paymentSaveData),
                    });

                    if (paymentSaveRes.ok) {
                      const paymentResult = await paymentSaveRes.json();
                    } else {
                      const paymentError = await paymentSaveRes.text();
                      console.error("❌ Payment entity save failed!");
                      console.error(
                        "  HTTP Status:",
                        paymentSaveRes.status,
                        paymentSaveRes.statusText
                      );
                      console.error("  Response body:", paymentError);
                      console.error(
                        "  Payload sent:",
                        JSON.stringify(paymentSaveData, null, 2)
                      );
                      console.warn("Payment entity save failed:", paymentError);
                      // Continue anyway since we still want to update the registration with payment info
                    }
                  } catch (paymentError) {
                    console.warn("Error saving payment entity:", paymentError);
                    // Continue anyway since we still want to update the registration with payment info
                  }

                  // Step 3: Confirm registration (marks as paid and confirmed)
                  const confirmUrl = `${REGISTRATION_BASE_URL}/registration/${registrationId}/confirm`;

                  // console.log("Confirming registration:", confirmUrl);

                  const confirmRes = await fetch(confirmUrl, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                  });

                  // console.log("Registration confirm response status:", confirmRes.status);

                  if (!confirmRes.ok) {
                    const errorText = await confirmRes.text();
                    //console.error("Registration confirmation failed:", errorText);
                    alert(
                      `Payment verified but confirming registration failed: ${errorText}`
                    );
                  } else {
                    // Show immediate success message
                    alert("Payment successful! Processing registration...");

                    // Process confirmation in background
                    const processConfirmation = async () => {
                      try {
                        const confirmed = await confirmRes.json();

                        setFormData((prev) => ({
                          ...prev,
                          paid: true,
                          id: confirmed.registrationId,
                          regId: confirmed.id,
                        }));

                        // Show final success message
                        setTimeout(() => {
                          alert("Registered successfully. Details Emailed");
                          setStep(1);
                        }, 500);
                      } catch (confirmError) {
                        console.error(
                          "Background confirmation error:",
                          confirmError
                        );
                        alert(
                          "Payment successful but confirmation pending. Check your email."
                        );
                      }
                    };

                    processConfirmation();
                  }
                } catch (err) {
                  console.error("Error in payment handler:", err);

                  // Capture this processing error as a failed payment
                  try {
                    await captureFailedPayment(
                      {
                        code: "PROCESSING_ERROR",
                        description: err.message,
                        source: "payment_handler",
                        step: "payment_processing",
                        reason: "processing_failed",
                      },
                      order
                    );
                  } catch (captureError) {
                    console.warn(
                      "Failed to capture payment processing error:",
                      captureError
                    );
                  }

                  // Reset payment initiated flag so user can try again
                  setPaymentInitiated(false);

                  // Show user-friendly message and redirect
                  alert(
                    "Payment processing failed. Please try again after sometime."
                  );

                  // Redirect to summary step
                  setTimeout(() => {
                    setStep(5);
                  }, 1000);
                }
              },
            }), // Close handler only for lower env
        prefill: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          contact: formData.mobile,
        },
        theme: { color: "#1976d2" },
      };

      const rzp = new window.Razorpay(options);
      razorpayInstanceRef.current = rzp; // Store reference

      rzp.on("payment.failed", async function (response) {
        console.error("Payment failed:", response.error);

        // Use ref to prevent any duplicate handling - this is crucial
        if (paymentFailureHandledRef.current) {
          return;
        }
        paymentFailureHandledRef.current = true;
        razorpayOpenRef.current = false; // Reset popup flag

        // Close the Razorpay popup immediately and clean up
        try {
          if (razorpayInstanceRef.current) {
            razorpayInstanceRef.current.close();
            razorpayInstanceRef.current = null;
          }
        } catch (closeError) {
          console.warn("Could not close Razorpay popup:", closeError);
        }

        // Additional state-based prevention
        if (paymentErrorShown) {
          return;
        }
        setPaymentErrorShown(true);

        try {
          // Capture failed payment details
          await captureFailedPayment(response.error, order);
        } catch (captureError) {
          console.warn(
            "Failed to capture failed payment details:",
            captureError
          );
        }

        // Reset payment initiated flag so user can try again
        setPaymentInitiated(false);

        // Show user-friendly message and redirect - use longer delay to prevent race conditions
        setTimeout(() => {
          if (paymentFailureHandledRef.current) {
            // Double check before showing alert
            alert(
              "Payment failed. Please try again after sometime or use a different payment method."
            );

            // Redirect to summary step
            setTimeout(() => {
              setStep(5);
            }, 500);
          }
        }, 200);
      });

      // Prevent multiple popups from opening
      if (razorpayOpenRef.current) {
        setProcessingPayment(false);
        setPaymentInitiated(false);
        return;
      }

      razorpayOpenRef.current = true;
      rzp.open();
    } catch (err) {
      console.error("Payment error", err);

      // Prevent duplicate error alerts - check both ref and state
      if (!paymentFailureHandledRef.current && !paymentErrorShown) {
        paymentFailureHandledRef.current = true;
        setPaymentErrorShown(true);
        razorpayOpenRef.current = false; // Reset popup flag

        // Capture payment initialization failure
        try {
          await captureFailedPayment(
            {
              code: "INITIALIZATION_ERROR",
              description: err.message,
              source: "payment_initialization",
              step: "payment_setup",
              reason: "initialization_failed",
            },
            {
              currency: currency,
              amount: amountInSmallestUnit,
            }
          );
        } catch (captureError) {
          console.warn(
            "Failed to capture payment initialization error:",
            captureError
          );
        }

        // Show user-friendly message and redirect
        setTimeout(() => {
          alert(
            "Payment setup failed. Please try again after sometime or check your details."
          );

          // Redirect to step 1 to check details
          setTimeout(() => {
            setStep(1);
          }, 500);
        }, 200);
      } else {
      }

      setPaymentInitiated(false); // Reset flag on error
    } finally {
      setProcessingPayment(false);
      // Ensure popup flag is reset in finally block
      if (razorpayOpenRef.current) {
        razorpayOpenRef.current = false;
      }
    }
  };

  // Function to capture failed payment details
  const captureFailedPayment = async (error, orderData) => {
    try {
      // Ensure we have a registration ID
      let registrationId = formData.id;
      let registrationDbId = formData.regId;

      if (!registrationId) {
        try {
          // Map to new v2 API format with user profile data for new user creation
          const conferenceRegistrationData = {
            // User identification
            email: formData.email,

            // User profile data (for new user creation if needed)
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: formData.mobile,
            country: formData.country,
            state: formData.state,
            city: formData.city,
            address: formData.address,
            zipCode: formData.pincode,
            affiliation: formData.affiliation,
            institution: formData.institution,
            // Conference details
            conference: formData.conference || conferenceName || currentHost,
            prefix: formData.prefix || "",
            fullNameCertificate: formData.fullNameCertificate,
            mciRegistered: formData.mciRegistered || "No",
            mciName: formData.mciName || "",
            mciNumber: formData.mciNumber || "",
            department: formData.department || formData.institution,
            departmentId: formData.departmentId,
            otherDepartment: formData.otherDepartment,
            category: formData.category || "TBD",
            categoryId: formData.categoryId,
            registrationType: formData.registrationType || "TBD",
            registrationTypeId: formData.registrationTypeId,
            membershipNumber: formData.membershipNumber || "",
            // Don't send base64 data to studentIdURL - it's too large and handled separately
            studentIdURL:
              formData.studentIdCard &&
              !formData.studentIdCard.startsWith("data:")
                ? formData.studentIdCard
                : null,
            specialRequirements: formData.specialRequirements || "",
            dietaryRestrictions: formData.dietaryRestrictions || "",
            accommodationNeeded: formData.accommodationNeeded || false,
            notes: formData.notes || "",
          };

          // Determine if this is an update or create operation
          const isUpdate = !!(formData.id || formData.regId);
          const method = isUpdate ? "PUT" : "POST";
          // Use regId if available, otherwise use id as fallback
          const updateRegistrationId = formData.regId || formData.id;
          const url = isUpdate
            ? `${CONFERENCE_REGISTRATION_URL}/${updateRegistrationId}`
            : CONFERENCE_REGISTRATION_URL;

          const regSaveRes = await fetch(url, {
            method: method,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(conferenceRegistrationData),
          });

          if (regSaveRes.ok) {
            const savedReg = await regSaveRes.json();
            registrationId = savedReg.registrationId;
            registrationDbId = savedReg.dbId || savedReg.id;

            setFormData((prev) => ({
              ...prev,
              id: savedReg.registrationId,
              regId: savedReg.dbId || savedReg.id,
            }));
          }
        } catch (regError) {
          console.warn(
            "Could not save registration for failed payment:",
            regError
          );
        }
      }

      const failedPaymentData = {
        regId: registrationDbId,
        razorpayOrderId: orderData?.id || orderData?.orderId || "unknown",
        razorpayPaymentId: null,
        razorpaySignature: null,
        status: "FAILED",
        paymentStatus: "FAILED",
        amount: orderData?.amount || 0,
        amountPaid: 0,
        currency:
          orderData?.currency || (formData.country === "India" ? "INR" : "USD"),
        paymentMethod: "razorpay",
        paymentGateway: "Razorpay",
        transactionTime: new Date().toISOString(),
        failureTime: new Date().toISOString(),
        orderAmount: orderData?.amount ? orderData.amount / 100 : 0,
        gatewayTransactionId: null,
        errorCode: error?.code || "UNKNOWN_ERROR",
        errorDescription:
          error?.description || error?.message || "Unknown error",
        errorSource: error?.source || "razorpay",
        errorStep: error?.step || "payment_gateway",
        errorReason: error?.reason || "payment_failed",
        metadata: JSON.stringify({
          userAgent: navigator.userAgent,
          timestamp: Date.now(),
          errorDetails: {
            code: error?.code,
            description: error?.description,
            source: error?.source,
            step: error?.step,
            reason: error?.reason,
          },
          orderInfo: {
            orderId: orderData?.id || orderData?.orderId,
            amount: orderData?.amount,
            currency: orderData?.currency,
          },
          userInfo: {
            email: formData.email,
            mobile: formData.mobile,
            country: formData.country,
            name: `${formData.firstName} ${formData.lastName}`.trim(),
          },
        }),
      };

      const failedPaymentRes = await fetch(PAYMENT_SAVE_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(failedPaymentData),
      });

      if (failedPaymentRes.ok) {
        const result = await failedPaymentRes.json();
      } else {
        const errorText = await failedPaymentRes.text();
        console.warn("Failed to capture failed payment:", errorText);
      }
    } catch (captureError) {
      console.error("Error capturing failed payment:", captureError);
    }
  };

  const loadRazorpayScript = () =>
    new Promise((resolve, reject) => {
      if (window.Razorpay) return resolve(true);
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        console.error("Failed to load Razorpay script");
        reject(new Error("Razorpay SDK failed to load."));
      };
      document.body.appendChild(script);
    });

  // Check if registration has been saved (has ID)
  const hasRegistrationId = !!(formData.id || formData.regId);

  // Step fields
  const renderStepFields = () => {
    switch (step) {
      case 1:
        return (
          <React.Fragment>
            <div className="grid-item">
              <select
                name="title"
                id="title"
                value={formData.title}
                onChange={handleChange}
              >
                <option value="" disabled hidden>
                  Title
                </option>
                <option value="Dr.">Dr.</option>
                <option value="Mr.">Mr.</option>
                <option value="Ms.">Ms.</option>
                <option value="Mrs.">Mrs.</option>
                <option value="Prof.">Prof.</option>
              </select>
            </div>
            <div className="grid-item">
              <input
                name="firstName"
                placeholder="First Name *"
                value={formData.firstName}
                onChange={handleChange}
              />
              {errors.firstName && (
                <p className="error-text">{errors.firstName}</p>
              )}
            </div>
            <div className="grid-item">
              <input
                name="lastName"
                placeholder="Last Name *"
                value={formData.lastName}
                onChange={handleChange}
              />
              {errors.lastName && (
                <p className="error-text">{errors.lastName}</p>
              )}
            </div>
            <div className="grid-item">
              <input
                name="email"
                placeholder="Email *"
                value={formData.email}
                onChange={handleChange}
              />
              {errors.email && <p className="error-text">{errors.email}</p>}
            </div>
            <div className="grid-item">
              <input
                type="tel"
                name="mobile"
                placeholder="Phone/Mobile Number *"
                value={formData.mobile}
                onChange={handleChange}
                maxLength={MOBILE_MAX_LENGTH}
                inputMode="numeric"
                pattern="[0-9]*"
              />
              {errors.mobile && <p className="error-text">{errors.mobile}</p>}
            </div>
            {genderOptions.length > 0 && (
              <div className="grid-item">
                <select
                  name="gender"
                  id="gender"
                  value={formData.gender}
                  onChange={handleChange}
                >
                  <option value="" disabled hidden>
                    Select Gender
                  </option>
                  {genderOptions.map((option) => (
                    <option key={option.id} value={option.value}>
                      {option.value}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </React.Fragment>
        );
      case 2:
        return (
          <React.Fragment>
            <div className="grid-item">
              <Select
                classNamePrefix="country-select"
                options={countries}
                value={
                  countries.find((c) => c.value === formData.country) || null
                }
                onChange={handleCountryChange}
                placeholder="Country *"
                isClearable
              />
              {errors.country && <p className="error-text">{errors.country}</p>}
            </div>
            <div className="grid-item">
              <input
                name="state"
                placeholder="State"
                value={formData.state}
                onChange={handleChange}
              />
            </div>
            <div className="grid-item">
              <input
                name="city"
                placeholder="City *"
                value={formData.city}
                onChange={handleChange}
                required
              />
            </div>
            <div className="grid-item">
              <input
                name="pincode"
                placeholder="Pincode *"
                value={formData.pincode}
                onChange={handleChange}
                required
              />
            </div>
            <div className="grid-item grid-item--full">
              <input
                name="address"
                placeholder="Address *"
                value={formData.address}
                onChange={handleChange}
                required
              />
            </div>
          </React.Fragment>
        );
      case 3:
        return (
          <>
            <div className="grid-item">
              <input
                name="affiliation"
                placeholder="Affiliation *"
                value={formData.affiliation}
                onChange={handleChange}
                required
              />
            </div>
            <div className="grid-item">
              <input
                name="institution"
                placeholder="Institution or Hospital Name *"
                value={formData.institution}
                onChange={handleChange}
                required
              />
            </div>
            {/* <div className="grid-item grid-item--radio">
              <label className="radio-label">Do you have MCI Number?</label>
              <div className="radio-row">
                <label>
                  <input
                    type="radio"
                    name="mciRegistered"
                    value="Yes"
                    checked={formData.mciRegistered === "Yes"}
                    onChange={handleChange}
                  />{" "}
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="mciRegistered"
                    value="No"
                    checked={formData.mciRegistered === "No"}
                    onChange={handleChange}
                  />{" "}
                  No
                </label>
              </div>
              {formData.mciRegistered === "Yes" && (
                <input
                  name="mciNumber"
                  placeholder="Enter MCI Number *"
                  value={formData.mciNumber}
                  onChange={handleChange}
                  required
                />
              )}
            </div> */}
          </>
        );
      case 4:
      case 5:
        return (
          <>
            {/* STEP 4 - Input Fields */}
            {step === 4 && (
              <>
                {/* Mandatory Fields Notice - Only show if fields exist */}
                {(categories.length > 0 || registrationTypes.length > 0) && (
                  <div
                    className="grid-item grid-item--full"
                    style={{ marginBottom: "15px" }}
                  >
                    <div
                      style={{
                        padding: "12px 16px",
                        backgroundColor: "#fff3cd",
                        border: "1px solid #ffc107",
                        borderRadius: "6px",
                        fontSize: "14px",
                        color: "#856404",
                      }}
                    >
                      <strong>⚠️ Required Information:</strong> Please select{" "}
                      {categories.length > 0 && registrationTypes.length > 0 ? (
                        <>
                          both <strong>Category</strong> and{" "}
                          <strong>Registration Type</strong>
                        </>
                      ) : categories.length > 0 ? (
                        <strong>Category</strong>
                      ) : (
                        <strong>Registration Type</strong>
                      )}{" "}
                      to proceed.{" "}
                      {categories.length > 0 && registrationTypes.length > 0
                        ? "These fields are"
                        : "This field is"}{" "}
                      mandatory.
                    </div>
                  </div>
                )}

                {departments.length > 0 && (
                  <>
                    <div className="grid-item">
                      <select
                        name="department"
                        value={formData.departmentId || ""}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const selectedDept = departments.find(
                            (d) => d.id === parseInt(selectedId)
                          );
                          setFormData((prev) => ({
                            ...prev,
                            departmentId: selectedId
                              ? parseInt(selectedId)
                              : null,
                            department: selectedDept ? selectedDept.value : "",
                          }));
                        }}
                      >
                        <option value="">Department</option>
                        {departments.map((dept) => (
                          <option key={dept.id} value={dept.id}>
                            {dept.value}
                          </option>
                        ))}
                      </select>
                    </div>
                    {formData.department === "Others" && (
                      <div className="grid-item">
                        <input
                          name="otherDepartment"
                          placeholder="Please specify department"
                          value={formData.otherDepartment}
                          onChange={handleChange}
                        />
                      </div>
                    )}
                  </>
                )}
                {categories.length > 0 && (
                  <>
                    <div className="grid-item grid-item--full">
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          color: "#000",
                          fontWeight: "500",
                          fontSize: "14px",
                        }}
                      >
                        Category <span style={{ color: "#dc3545" }}>*</span>
                      </label>
                      <select
                        name="category"
                        value={formData.categoryId || ""}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const selectedCat = categories.find(
                            (c) => c.id === parseInt(selectedId)
                          );

                          setFormData((prev) => {
                            // Save current values before switching
                            if (
                              prev.category === "ISCCM(Members)" &&
                              prev.membershipNumber
                            ) {
                              setSavedMembershipNumber(prev.membershipNumber);
                            }
                            if (
                              (prev.category === "Student" ||
                                prev.category === "student") &&
                              prev.studentIdCard
                            ) {
                              setSavedStudentIdCard(prev.studentIdCard);
                              setSavedStudentIdCardName(prev.studentIdCardName);
                              setSavedStudentIdCardType(prev.studentIdCardType);
                            }

                            // Determine values for new category
                            let membershipNumber = "";
                            let studentIdCard = null;
                            let studentIdCardName = "";
                            let studentIdCardType = "";

                            if (
                              selectedCat &&
                              selectedCat.value === "ISCCM(Members)"
                            ) {
                              // Restore saved membership number if available
                              membershipNumber =
                                savedMembershipNumber ||
                                prev.membershipNumber ||
                                "";
                            } else if (
                              selectedCat &&
                              (selectedCat.value === "Student" ||
                                selectedCat.value === "student")
                            ) {
                              // Restore saved student ID card if available
                              studentIdCard =
                                savedStudentIdCard ||
                                prev.studentIdCard ||
                                null;
                              studentIdCardName =
                                savedStudentIdCardName ||
                                prev.studentIdCardName ||
                                "";
                              studentIdCardType =
                                savedStudentIdCardType ||
                                prev.studentIdCardType ||
                                "";
                            }

                            return {
                              ...prev,
                              categoryId: selectedId
                                ? parseInt(selectedId)
                                : null,
                              category: selectedCat ? selectedCat.value : "",
                              membershipNumber,
                              studentIdCard,
                              studentIdCardName,
                              studentIdCardType,
                            };
                          });

                          // Clear file input and errors when not Student category or registration type
                          const isStudentSelected =
                            selectedCat &&
                            selectedCat.value.toLowerCase().includes("student");
                          const isStudentRegType =
                            formData.registrationType &&
                            formData.registrationType
                              .toLowerCase()
                              .includes("student");
                          if (!isStudentSelected && !isStudentRegType) {
                            setStudentIdCardFile(null);
                            setStudentIdCardError("");
                          }
                        }}
                        required
                        style={{
                          borderColor: !formData.category ? "#dc3545" : "",
                        }}
                      >
                        <option value="" disabled hidden>
                          Select Category
                        </option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.value}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
                {registrationTypes.length > 0 && (
                  <>
                    <div className="grid-item">
                      <label
                        style={{
                          display: "block",
                          marginBottom: "5px",
                          color: "#000",
                          fontWeight: "500",
                          fontSize: "14px",
                        }}
                      >
                        Registration Type{" "}
                        <span style={{ color: "#dc3545" }}>*</span>
                      </label>
                      <select
                        name="registrationType"
                        value={formData.registrationTypeId || ""}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          const selectedType = registrationTypes.find(
                            (rt) => rt.id === parseInt(selectedId)
                          );
                          setFormData((prev) => ({
                            ...prev,
                            registrationTypeId: selectedId
                              ? parseInt(selectedId)
                              : null,
                            registrationType: selectedType
                              ? selectedType.value
                              : "",
                          }));

                          // Clear file input and errors when not Student registration type or category
                          const isStudentRegType =
                            selectedType &&
                            selectedType.value
                              .toLowerCase()
                              .includes("student");
                          const isStudentCategory =
                            formData.category &&
                            formData.category.toLowerCase().includes("student");
                          if (!isStudentRegType && !isStudentCategory) {
                            setStudentIdCardFile(null);
                            setStudentIdCardError("");
                          }
                        }}
                        required
                        style={{
                          borderColor: !formData.registrationType
                            ? "#dc3545"
                            : "",
                        }}
                      >
                        <option value="" disabled hidden>
                          Select Registration Type
                        </option>
                        {registrationTypes.map((rt) => (
                          <option key={rt.id} value={rt.id}>
                            {rt.value}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {/* Membership Number - show before Accompanying Persons */}
                {formData.category === "Delegate (Members)" && (
                  <div className="grid-item grid-item--full">
                    <input
                      name="membershipNumber"
                      placeholder="Enter Membership Number *"
                      value={formData.membershipNumber}
                      onChange={handleChange}
                      required
                      style={{
                        borderColor: !formData.membershipNumber
                          ? "#dc3545"
                          : "",
                      }}
                    />
                  </div>
                )}

                {/* ISA Membership Number - Only for ISA MEMBER, not NON-ISA MEMBER */}
                {formData.category &&
                  formData.category.toString().toLowerCase().includes("isa") &&
                  !formData.category.toString().toLowerCase().includes("non") &&
                  !formData.category
                    .toString()
                    .toLowerCase()
                    .includes("isccm") && (
                    <div className="grid-item grid-item--full">
                      <input
                        name="isaMembershipNumber"
                        placeholder="Enter ISA Membership Number *"
                        value={formData.isaMembershipNumber || ""}
                        onChange={handleChange}
                        required
                        style={{
                          borderColor: !formData.isaMembershipNumber
                            ? "#dc3545"
                            : "",
                        }}
                      />
                    </div>
                  )}

                {/* Student ID Card Upload */}
                {((formData.category &&
                  formData.category.toLowerCase().includes("student")) ||
                  (formData.registrationType &&
                    formData.registrationType
                      .toLowerCase()
                      .includes("student"))) && (
                  <div className="grid-item" style={{ gridColumn: "1 / -1" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontWeight: "500",
                      }}
                    >
                      Student ID Card * (Single file: JPEG or PDF, max 5MB)
                    </label>
                    <input
                      type="file"
                      name="studentIdCard"
                      accept=".jpg,.jpeg,.pdf,image/jpeg,application/pdf"
                      onChange={handleStudentIdCardUpload}
                      multiple={false}
                      style={{
                        width: "100%",
                        padding: "10px",
                        border: studentIdCardError
                          ? "1px solid #dc3545"
                          : "1px solid #ddd",
                        borderRadius: "4px",
                        fontSize: "14px",
                      }}
                    />
                    {studentIdCardError && (
                      <div
                        style={{
                          color: "#dc3545",
                          fontSize: "12px",
                          marginTop: "5px",
                        }}
                      >
                        {studentIdCardError}
                      </div>
                    )}
                    {/* Show success message for newly uploaded file */}
                    {formData.studentIdCardName && !studentIdCardError && (
                      <div
                        style={{
                          color: "#28a745",
                          fontSize: "12px",
                          marginTop: "5px",
                        }}
                      >
                        ✓ {formData.studentIdCardName}
                      </div>
                    )}
                    {/* Show success message for previously uploaded file from database */}
                    {!formData.studentIdCardName &&
                      formData.studentIdCard === "DATABASE" &&
                      !studentIdCardError && (
                        <div
                          style={{
                            color: "#28a745",
                            fontSize: "12px",
                            marginTop: "5px",
                          }}
                        >
                          ✓ Student ID card previously uploaded (on file)
                        </div>
                      )}
                    {/* Show info message when studentIdCard has a URL from database */}
                    {!formData.studentIdCardName &&
                      formData.studentIdCard &&
                      formData.studentIdCard !== "DATABASE" &&
                      !formData.studentIdCard.startsWith("data:") &&
                      !studentIdCardError && (
                        <div
                          style={{
                            color: "#28a745",
                            fontSize: "12px",
                            marginTop: "5px",
                          }}
                        >
                          ✓ Student ID card on file
                        </div>
                      )}
                  </div>
                )}

                {/* Accompanying Persons Section - Only show if fee is configured */}
                {accompanyingPersonFee && (
                  <div className="grid-item grid-item--full">
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        fontSize: "14px",
                        fontWeight: "500",
                        color: "#000000",
                      }}
                    >
                      Accompanying Persons{" "}
                      <span style={{ fontWeight: "400" }}>(Optional)</span>
                      <span
                        style={{
                          fontWeight: "400",
                          color: "#000000",
                          fontSize: "13px",
                        }}
                      >
                        {" "}
                        (Fee: {accompanyingPersonFee.currency}{" "}
                        {accompanyingPersonFee.rate} per person)
                      </span>
                    </label>

                    {accompanyingPersons.map((person, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginBottom: "8px",
                          padding: person.paid ? "8px" : "0",
                          backgroundColor: person.paid
                            ? "#f0f9ff"
                            : "transparent",
                          border: person.paid ? "2px solid #28a745" : "none",
                          borderRadius: person.paid ? "6px" : "0",
                          position: "relative",
                        }}
                      >
                        {person.paid && (
                          <div
                            style={{
                              position: "absolute",
                              top: "-1px",
                              right: "-1px",
                              backgroundColor: "#28a745",
                              color: "white",
                              padding: "2px 8px",
                              fontSize: "11px",
                              fontWeight: "bold",
                              borderTopRightRadius: "4px",
                              borderBottomLeftRadius: "4px",
                            }}
                          >
                            🔒 PAID
                          </div>
                        )}
                        <input
                          type="text"
                          placeholder={`Accompanying Person ${
                            index + 1
                          } Name *`}
                          value={person.name}
                          onChange={(e) =>
                            handleAccompanyingPersonNameChange(
                              index,
                              e.target.value
                            )
                          }
                          required
                          style={{
                            flex: 1,
                            backgroundColor: person.paid ? "#e8f5e9" : "white",
                            cursor: person.paid ? "not-allowed" : "text",
                            color: person.paid ? "#2e7d32" : "#000000",
                            fontWeight: person.paid ? "500" : "normal",
                            fontSize: "14px",
                            padding: "8px 12px",
                            border: "1px solid #ccc",
                            borderRadius: "4px",
                          }}
                          disabled={person.paid}
                          title={
                            person.paid
                              ? "⚠️ Payment completed - Cannot modify"
                              : ""
                          }
                          readOnly={person.paid}
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveAccompanyingPerson(index)}
                          disabled={person.paid}
                          style={{
                            padding: "8px 12px",
                            backgroundColor: person.paid
                              ? "#e0e0e0"
                              : "#ef4444",
                            color: person.paid ? "#9e9e9e" : "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: person.paid ? "not-allowed" : "pointer",
                            fontSize: "13px",
                            opacity: person.paid ? 0.5 : 1,
                            textDecoration: person.paid
                              ? "line-through"
                              : "none",
                          }}
                          title={
                            person.paid
                              ? "⚠️ Cannot remove - Payment completed and cannot be reversed"
                              : "Remove"
                          }
                        >
                          Remove
                        </button>
                      </div>
                    ))}

                    <button
                      type="button"
                      onClick={handleAddAccompanyingPerson}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: "#1976d2",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "13px",
                        marginTop: "4px",
                      }}
                    >
                      + Add Accompanying Person
                    </button>

                    {accompanyingPersons.some((p) => p.paid) && (
                      <div
                        style={{
                          marginTop: "8px",
                          padding: "10px",
                          backgroundColor: "#fff3cd",
                          border: "1px solid #ffc107",
                          borderRadius: "4px",
                          fontSize: "13px",
                          color: "#856404",
                        }}
                      >
                        <strong>⚠️ Important:</strong> Items marked as "PAID"
                        are locked because payment has been completed and cannot
                        be reversed. You can add new items which will require
                        additional payment.
                      </div>
                    )}

                    {formData.paid && accompanyingPersons.length === 0 && (
                      <p
                        style={{
                          fontSize: "13px",
                          color: "#059669",
                          fontStyle: "italic",
                          marginTop: "8px",
                        }}
                      >
                        ✓ Payment completed. You can still add more accompanying
                        persons if needed (additional payment required).
                      </p>
                    )}
                  </div>
                )}

                {/* Focused Sessions Section - Only show if there are focused sessions available */}
                {focusedSessions.length > 0 && (
                  <div className="grid-item grid-item--full">
                    <div>
                      <label
                        style={{
                          display: "block",
                          marginBottom: "8px",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        Master class selection
                      </label>
                      <div
                        className="grid-item grid-item--radio"
                        style={{ marginBottom: "10px" }}
                      >
                        <div
                          className="radio-row"
                          style={{ display: "flex", gap: "20px" }}
                        >
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="radio"
                              name="focusedSessions"
                              value="Yes"
                              checked={registerForFocusedSessions === "Yes"}
                              onChange={(e) =>
                                handleFocusedSessionsChange(e.target.value)
                              }
                              style={{ marginRight: "8px" }}
                            />
                            <span
                              style={{ fontSize: "14px", color: "#000000" }}
                            >
                              Yes
                            </span>
                          </label>
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="radio"
                              name="focusedSessions"
                              value="No"
                              checked={registerForFocusedSessions === "No"}
                              onChange={(e) =>
                                handleFocusedSessionsChange(e.target.value)
                              }
                              style={{ marginRight: "8px" }}
                            />
                            <span
                              style={{ fontSize: "14px", color: "#000000" }}
                            >
                              No
                            </span>
                          </label>
                        </div>
                      </div>
                      {registerForFocusedSessions === "Yes" && (
                        <div>
                          <div style={{ marginTop: "10px" }}>
                            {focusedSessions.length > 0 ? (
                              <div>
                                <p
                                  style={{
                                    fontSize: "13px",
                                    color: "#000000",
                                    marginBottom: "10px",
                                    fontWeight: "500",
                                  }}
                                >
                                  Please select the Master class selections you
                                  would like to attend:
                                </p>
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "15px",
                                  }}
                                >
                                  {(() => {
                                    // Group sessions by sessionDate (from MasterDefinitionDetailsEntity)
                                    const sessionsByDate =
                                      focusedSessions.reduce(
                                        (groups, session) => {
                                          // Use sessionDate field, fallback to dateGroup, then 'Other Sessions'
                                          const dateGroup =
                                            session.sessionDate ||
                                            session.dateGroup ||
                                            "Other Sessions";
                                          if (!groups[dateGroup]) {
                                            groups[dateGroup] = [];
                                          }
                                          groups[dateGroup].push(session);
                                          return groups;
                                        },
                                        {}
                                      );
                                    // Sort date groups in ascending order
                                    // Put 'Other Sessions' at the end
                                    const sortedDateGroups = Object.keys(
                                      sessionsByDate
                                    ).sort((a, b) => {
                                      if (a === "Other Sessions") return 1;
                                      if (b === "Other Sessions") return -1;
                                      return a.localeCompare(b);
                                    });
                                    return sortedDateGroups.map((dateGroup) => (
                                      <div
                                        key={dateGroup}
                                        style={{ marginBottom: "10px" }}
                                      >
                                        {/* Date Group Header */}
                                        <div
                                          style={{
                                            fontSize: "14px",
                                            fontWeight: "bold",
                                            color: "#000000",
                                            marginBottom: "8px",
                                            paddingBottom: "4px",
                                            borderBottom: "2px solid #e0e0e0",
                                          }}
                                        >
                                          📅 {dateGroup}
                                        </div>
                                        {/* Sessions for this date */}
                                        <div
                                          style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: "8px",
                                            paddingLeft: "10px",
                                          }}
                                        >
                                          {sessionsByDate[dateGroup].map(
                                            (session) => {
                                              // Check if this session is already paid for
                                              const isPaid =
                                                formData.additionalFees &&
                                                formData.additionalFees.some(
                                                  (fee) =>
                                                    fee.feeTypeId ===
                                                      session.id &&
                                                    fee.paymentId
                                                );
                                              return (
                                                <label
                                                  key={session.id}
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    cursor: isPaid
                                                      ? "not-allowed"
                                                      : "pointer",
                                                    padding: isPaid
                                                      ? "8px"
                                                      : "4px",
                                                    backgroundColor: isPaid
                                                      ? "#f0f9ff"
                                                      : "transparent",
                                                    border: isPaid
                                                      ? "2px solid #28a745"
                                                      : "none",
                                                    borderRadius: isPaid
                                                      ? "6px"
                                                      : "0",
                                                    position: "relative",
                                                  }}
                                                  title={
                                                    isPaid
                                                      ? "⚠️ Payment completed - Cannot be unchecked or reversed"
                                                      : "Select this session"
                                                  }
                                                >
                                                  {isPaid && (
                                                    <div
                                                      style={{
                                                        position: "absolute",
                                                        top: "-1px",
                                                        right: "-1px",
                                                        backgroundColor:
                                                          "#28a745",
                                                        color: "white",
                                                        padding: "2px 8px",
                                                        fontSize: "11px",
                                                        fontWeight: "bold",
                                                        borderTopRightRadius:
                                                          "4px",
                                                        borderBottomLeftRadius:
                                                          "4px",
                                                      }}
                                                    >
                                                      🔒 PAID
                                                    </div>
                                                  )}
                                                  <input
                                                    type="checkbox"
                                                    checked={selectedFocusedSessions.includes(
                                                      session.id
                                                    )}
                                                    onChange={() =>
                                                      handleFocusedSessionCheckboxChange(
                                                        session.id
                                                      )
                                                    }
                                                    disabled={isPaid}
                                                    style={{
                                                      marginRight: "8px",
                                                      marginTop: "2px",
                                                      cursor: isPaid
                                                        ? "not-allowed"
                                                        : "pointer",
                                                    }}
                                                  />
                                                  <span
                                                    style={{
                                                      fontSize: "14px",
                                                      color: isPaid
                                                        ? "#2e7d32"
                                                        : "inherit",
                                                      fontWeight: isPaid
                                                        ? "500"
                                                        : "normal",
                                                    }}
                                                  >
                                                    {session.value}
                                                  </span>
                                                </label>
                                              );
                                            }
                                          )}
                                        </div>
                                      </div>
                                    ));
                                  })()}
                                </div>
                                {formData.additionalFees &&
                                  formData.additionalFees.some(
                                    (fee) =>
                                      fee.feeTypeId !==
                                        accompanyingPersonFee?.id &&
                                      fee.paymentId
                                  ) && (
                                    <div
                                      style={{
                                        marginTop: "12px",
                                        padding: "10px",
                                        backgroundColor: "#fff3cd",
                                        border: "1px solid #ffc107",
                                        borderRadius: "4px",
                                        fontSize: "13px",
                                        color: "#856404",
                                      }}
                                    >
                                      <strong>⚠️ Important:</strong> Sessions
                                      marked as "PAID" are locked because
                                      payment has been completed and cannot be
                                      reversed. You can select new sessions
                                      which will require additional payment.
                                    </div>
                                  )}
                              </div>
                            ) : (
                              <p
                                style={{
                                  fontSize: "13px",
                                  color: "#666",
                                  fontStyle: "italic",
                                }}
                              >
                                No focused sessions available.
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* STEP 5 - Summary/Review */}
            {step === 5 && (
              <div className="summary-container">
                {/* Personal Information Section */}
                <div className="summary-section">
                  <h4>Personal Information</h4>
                  {formData.title && (
                    <div className="summary-row">
                      <span className="summary-label">Title:</span>
                      <span className="summary-value">{formData.title}</span>
                    </div>
                  )}
                  <div className="summary-row">
                    <span className="summary-label">Name:</span>
                    <span className="summary-value">
                      {formData.firstName} {formData.lastName}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Email:</span>
                    <span className="summary-value">{formData.email}</span>
                  </div>
                  <div className="summary-row">
                    <span className="summary-label">Mobile:</span>
                    <span className="summary-value">{formData.mobile}</span>
                  </div>
                </div>

                {/* Address Information Section */}
                <div className="summary-section">
                  <h4>Address Information</h4>
                  <div className="summary-row">
                    <span className="summary-label">Country:</span>
                    <span className="summary-value">{formData.country}</span>
                  </div>
                  {formData.state && (
                    <div className="summary-row">
                      <span className="summary-label">State:</span>
                      <span className="summary-value">{formData.state}</span>
                    </div>
                  )}
                  {formData.city && (
                    <div className="summary-row">
                      <span className="summary-label">City:</span>
                      <span className="summary-value">{formData.city}</span>
                    </div>
                  )}
                  {formData.pincode && (
                    <div className="summary-row">
                      <span className="summary-label">Pincode:</span>
                      <span className="summary-value">{formData.pincode}</span>
                    </div>
                  )}
                  {formData.address && (
                    <div className="summary-row">
                      <span className="summary-label">Address:</span>
                      <span className="summary-value">{formData.address}</span>
                    </div>
                  )}
                </div>

                <div className="summary-section">
                  <h4>Professional Information</h4>
                  {formData.affiliation && (
                    <div className="summary-row">
                      <span className="summary-label">Affiliation:</span>
                      <span className="summary-value">
                        {formData.affiliation}
                      </span>
                    </div>
                  )}
                  {formData.institution && (
                    <div className="summary-row">
                      <span className="summary-label">Institution:</span>
                      <span className="summary-value">
                        {formData.institution}
                      </span>
                    </div>
                  )}
                  {/* <div className="summary-row">
                    <span className="summary-label">MCI Registered:</span>
                    <span className="summary-value">
                      {formData.mciRegistered}
                    </span>
                  </div> */}
                  {/* {formData.mciRegistered === "Yes" && formData.mciNumber && (
                    <div className="summary-row">
                      <span className="summary-label">MCI Number:</span>
                      <span className="summary-value">
                        {formData.mciNumber}
                      </span>
                    </div>
                  )} */}
                </div>

                <div className="summary-section">
                  <h4>Registration Details</h4>

                  {/* {formData.department && (
                    <div className="summary-row">
                      <span className="summary-label">Department:</span>
                      <span className="summary-value">
                        {formData.department === "Others"
                          ? formData.otherDepartment
                          : formData.department}
                      </span>
                    </div>
                  )} */}
                  {formData.category && (
                    <div className="summary-row">
                      <span className="summary-label">Category:</span>
                      <span className="summary-value">{formData.category}</span>
                    </div>
                  )}
                  {formData.category === "ISCCM(Members)" &&
                    formData.membershipNumber && (
                      <div className="summary-row">
                        <span className="summary-label">
                          Membership Number:
                        </span>
                        <span className="summary-value">
                          {formData.membershipNumber}
                        </span>
                      </div>
                    )}
                  {formData.category &&
                    formData.category
                      .toString()
                      .toLowerCase()
                      .includes("isa") &&
                    !formData.category
                      .toString()
                      .toLowerCase()
                      .includes("isccm") &&
                    formData.isaMembershipNumber && (
                      <div className="summary-row">
                        <span className="summary-label">
                          ISA Membership Number:
                        </span>
                        <span className="summary-value">
                          {formData.isaMembershipNumber}
                        </span>
                      </div>
                    )}
                  {((formData.category &&
                    formData.category.toLowerCase().includes("student")) ||
                    (formData.registrationType &&
                      formData.registrationType
                        .toLowerCase()
                        .includes("student"))) &&
                    (formData.studentIdCard ||
                      formData.studentIdCardUploaded) && (
                      <div className="summary-row">
                        <span className="summary-label">Student ID Card:</span>
                        <span className="summary-value">
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              let fileUrl;

                              // Priority 1: If we have a registration ID and file was uploaded or marked as DATABASE
                              if (
                                formData.regId &&
                                (formData.studentIdCardUploaded ||
                                  formData.studentIdCard === "DATABASE" ||
                                  !formData.studentIdCard.startsWith("data:"))
                              ) {
                                fileUrl = `${REGISTRATION_BASE_URL}/registration/${formData.regId}/file/id-card`;
                                console.log(
                                  "📥 Opening from database:",
                                  fileUrl
                                );
                              }
                              // Priority 2: If student ID card is base64 data (preview before save)
                              else if (
                                formData.studentIdCard &&
                                formData.studentIdCard.startsWith("data:")
                              ) {
                                fileUrl = formData.studentIdCard;
                                console.log("📥 Opening base64 preview");
                              }
                              // Priority 3: Legacy - If it's an old URL path
                              else if (
                                formData.studentIdCard &&
                                formData.studentIdCard.startsWith("http")
                              ) {
                                fileUrl = formData.studentIdCard;
                                console.log("📥 Opening legacy URL:", fileUrl);
                              }
                              // Priority 4: Legacy - If it's a path (old file system storage)
                              else if (formData.studentIdCard) {
                                const path = formData.studentIdCard.startsWith(
                                  "/"
                                )
                                  ? formData.studentIdCard
                                  : `/${formData.studentIdCard}`;
                                fileUrl = `${REGISTRATION_BASE_URL}${path}`;
                                console.log("📥 Opening legacy path:", fileUrl);
                              }

                              if (fileUrl) {
                                window.open(
                                  fileUrl,
                                  "_blank",
                                  "noopener,noreferrer"
                                );
                              } else {
                                console.error("❌ No file URL available");
                                alert(
                                  "Unable to open document. Please try uploading again."
                                );
                              }
                            }}
                            style={{
                              color: "#0066cc",
                              textDecoration: "underline",
                              cursor: "pointer",
                            }}
                          >
                            View Uploaded Document
                          </a>
                        </span>
                      </div>
                    )}
                  {/* {formData.registrationType && (
                    <div className="summary-row">
                      <span className="summary-label">Registration Type:</span>
                      <span className="summary-value">
                        {formData.registrationType}
                      </span>
                    </div>
                  )} */}
                  <div className="summary-row">
                    <span className="summary-label">Conference:</span>
                    <span className="summary-value">
                      {formData.conference || conferenceName || currentHost}
                    </span>
                  </div>
                </div>

                {/* Registration Fee Section */}
                {formData.categoryId && (
                  <div className="summary-section">
                    <h4>Registration Fee</h4>
                    {feeLoading ? (
                      <div className="summary-row">
                        <span className="summary-label">Fee:</span>
                        <span className="summary-value">⏳ Calculating...</span>
                      </div>
                    ) : feeError ? (
                      <div className="summary-row fee-error-row">
                        <span className="summary-label">Fee:</span>
                        <span className="summary-value">⚠️ {feeError}</span>
                      </div>
                    ) : registrationFee ? (
                      <>
                        <div className="summary-row">
                          <span className="summary-label">Amount:</span>
                          <span className="summary-value">
                            {formData.country === "India" ? "₹" : "$"}
                            {parseFloat(registrationFee.rate).toFixed(2)} (
                            {registrationFee.currency})
                          </span>
                        </div>
                        {registrationFee.feeCategory && (
                          <div className="summary-row">
                            <span className="summary-label">Fee Type:</span>
                            <span className="summary-value">
                              {registrationFee.feeCategory.value}
                            </span>
                          </div>
                        )}
                        {registrationFee.validFrom &&
                          registrationFee.validTo && (
                            <div className="summary-row">
                              <span className="summary-label">
                                Valid Period:
                              </span>
                              <span className="summary-value">
                                {new Date(
                                  registrationFee.validFrom
                                ).toLocaleDateString()}{" "}
                                -{" "}
                                {new Date(
                                  registrationFee.validTo
                                ).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                      </>
                    ) : null}
                  </div>
                )}

                {/* Accompanying Persons Section */}
                {(() => {
                  // Use accompanyingPersons state as the source of truth
                  // This ensures that if user removes someone in step 4, it won't show in step 5

                  // Map state items and enrich with payment status from database
                  const allAccompanyingPersons = accompanyingPersons.map(
                    (person) => {
                      // Check if this person is already paid (has paymentId)
                      const isPaid = person.paid || !!person.paymentId;

                      // Try to get payment details from additionalFees if available
                      const paidFee = formData.additionalFees?.find(
                        (fee) =>
                          accompanyingPersonFee &&
                          fee.feeTypeId === accompanyingPersonFee.id &&
                          fee.name === person.name &&
                          fee.paymentId
                      );

                      // Try to get from zeroPayments if not paid
                      const savedZero = formData.zeroPayments?.find(
                        (zp) =>
                          accompanyingPersonFee &&
                          zp.feeTypeId === accompanyingPersonFee.id &&
                          zp.name === person.name
                      );

                      return {
                        name: person.name,
                        paid: isPaid || !!paidFee,
                        saved: !!savedZero && !isPaid && !paidFee,
                        paymentId: person.paymentId || paidFee?.paymentId,
                        source: paidFee ? "paid" : savedZero ? "saved" : "new",
                        rate: paidFee?.rate || accompanyingPersonFee?.rate || 0,
                        currency:
                          paidFee?.currency ||
                          accompanyingPersonFee?.currency ||
                          registrationFee?.currency ||
                          "INR",
                      };
                    }
                  );

                  console.log("🔍 Accompanying Persons Display (from state):");
                  console.log(
                    "  - accompanyingPersons state:",
                    accompanyingPersons
                  );
                  console.log(
                    "  - Final list with payment status:",
                    allAccompanyingPersons
                  );

                  if (allAccompanyingPersons.length === 0) return null;

                  // Get rate and currency from accompanyingPersonFee or from the first item
                  const displayRate =
                    accompanyingPersonFee?.rate ||
                    allAccompanyingPersons[0]?.rate ||
                    0;
                  const displayCurrency =
                    accompanyingPersonFee?.currency ||
                    allAccompanyingPersons[0]?.currency ||
                    registrationFee?.currency ||
                    "INR";

                  return (
                    <div className="summary-section">
                      <h4>Accompanying Persons</h4>
                      {allAccompanyingPersons.map((person, index) => (
                        <div className="summary-row" key={index}>
                          <span className="summary-label">
                            Person {index + 1}:
                          </span>
                          <span className="summary-value">
                            {person.name}
                            {person.paid && (
                              <span
                                style={{
                                  marginLeft: "8px",
                                  color: "#28a745",
                                  fontSize: "12px",
                                }}
                              >
                                ✓ Paid
                              </span>
                            )}
                            {person.saved && !person.paid && (
                              <span
                                style={{
                                  marginLeft: "8px",
                                  color: "#0d6efd",
                                  fontSize: "12px",
                                }}
                              >
                                ✓ Saved
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                      {accompanyingPersonFee && (
                        <>
                          <div className="summary-row">
                            <span className="summary-label">
                              Fee per Person:
                            </span>
                            <span className="summary-value">
                              {formData.country === "India" ? "₹" : "$"}
                              {parseFloat(displayRate).toFixed(2)} (
                              {displayCurrency})
                            </span>
                          </div>
                          <div className="summary-row">
                            <span className="summary-label">
                              Subtotal ({allAccompanyingPersons.length} person
                              {allAccompanyingPersons.length > 1 ? "s" : ""}):
                            </span>
                            <span
                              className="summary-value"
                              style={{ fontWeight: "bold" }}
                            >
                              {formData.country === "India" ? "₹" : "$"}
                              {(
                                parseFloat(displayRate) *
                                allAccompanyingPersons.length
                              ).toFixed(2)}{" "}
                              ({displayCurrency})
                            </span>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}

                {/* Focused Sessions Section - PAID SESSIONS ONLY */}
                {(() => {
                  // Get accompanyingFeeId if available
                  const accompanyingFeeId = accompanyingPersonFee?.id;

                  // Get ONLY PAID focused sessions from additionalFees (exclude free ones and accompanying persons)
                  const paidFocusedSessions = formData.additionalFees
                    ? accompanyingFeeId
                      ? formData.additionalFees.filter(
                          (fee) => fee.feeTypeId !== accompanyingFeeId
                        )
                      : formData.additionalFees // If no accompanyingFeeId, show all as potential sessions
                    : [];

                  console.log(
                    "📊 Display logic - Focused Sessions (Paid Only):"
                  );
                  console.log("  - accompanyingFeeId:", accompanyingFeeId);
                  console.log(
                    "  - Paid sessions from additionalFees:",
                    paidFocusedSessions
                  );
                  console.log(
                    "  - Currently selected (state):",
                    selectedFocusedSessions
                  );

                  // Get IDs of paid sessions from database
                  const paidSessionIds = paidFocusedSessions.map(
                    (fee) => fee.feeTypeId
                  );

                  // Add newly selected sessions that are NOT yet in database
                  const newlySelectedSessionIds =
                    selectedFocusedSessions.filter((sessionId) => {
                      // Check if NOT in paid sessions
                      const notInPaid = !paidSessionIds.includes(sessionId);
                      // Check if NOT in zero payments (we don't want free ones here)
                      const notInFree = !formData.zeroPayments?.some(
                        (zp) => zp.feeTypeId === sessionId
                      );
                      // Check if NOT the accompanying person fee
                      const notAccompanying =
                        !accompanyingFeeId || sessionId !== accompanyingFeeId;
                      return notInPaid && notInFree && notAccompanying;
                    });

                  // Combine: paid sessions from DB + newly selected (not yet saved, not free)
                  const allPaidSessionIds = [
                    ...paidSessionIds,
                    ...newlySelectedSessionIds,
                  ];

                  console.log("  - Paid session IDs:", paidSessionIds);
                  console.log(
                    "  - Newly selected (not saved, not free):",
                    newlySelectedSessionIds
                  );
                  console.log("  - Final list (paid only):", allPaidSessionIds);

                  if (allPaidSessionIds.length === 0) return null;

                  return (
                    <div className="summary-section">
                      <h4>Focused Sessions</h4>
                      {allPaidSessionIds.map((sessionId, index) => {
                        // Try to get name and rate from additionalFees first (most accurate for paid items)
                        const paidSession = paidFocusedSessions.find(
                          (ps) => ps.feeTypeId === sessionId
                        );

                        // Get session name from multiple sources - ENSURE we get a string, not an object
                        let sessionName = null;

                        // 1. Try from paidSession (additionalFees)
                        if (
                          paidSession &&
                          paidSession.name &&
                          typeof paidSession.name === "string"
                        ) {
                          sessionName = paidSession.name;
                        }

                        // 2. Try from focusedSessions array
                        if (!sessionName) {
                          const sessionObj = focusedSessions.find(
                            (s) => s.id === sessionId
                          );
                          if (
                            sessionObj &&
                            sessionObj.value &&
                            typeof sessionObj.value === "string"
                          ) {
                            sessionName = sessionObj.value;
                          }
                        }

                        // 3. Try from zeroPayments
                        if (!sessionName && formData.zeroPayments) {
                          const zeroPayment = formData.zeroPayments.find(
                            (zp) => zp.feeTypeId === sessionId
                          );
                          if (
                            zeroPayment &&
                            zeroPayment.name &&
                            typeof zeroPayment.name === "string"
                          ) {
                            sessionName = zeroPayment.name;
                          }
                        }

                        // 4. Fallback to session ID
                        if (!sessionName) {
                          sessionName = `Session ${sessionId}`;
                        }

                        // For display: use rate from paidSession (from DB) if available, otherwise from state
                        const sessionFee = focusedSessionFees[sessionId];
                        // CRITICAL: Only mark as "paid" if paymentId exists (not just saved in additionalFees)
                        const isPaid = paidSession && !!paidSession.paymentId;
                        const isSaved = paidSession && !paidSession.paymentId; // Saved but not paid yet

                        // Get the actual rate and currency
                        let displayRate, displayCurrency;
                        if ((isPaid || isSaved) && paidSession) {
                          // Use rate from database (additionalFees), fallback to API fee if null
                          displayRate =
                            paidSession.rate ||
                            (sessionFee ? sessionFee.rate : 0);
                          displayCurrency =
                            paidSession.currency ||
                            registrationFee?.currency ||
                            "INR";
                        } else if (sessionFee) {
                          // Use rate from fetched fee (for new selections)
                          displayRate = sessionFee.rate;
                          displayCurrency = sessionFee.currency || "INR";
                        } else {
                          // Fallback to 0
                          displayRate = 0;
                          displayCurrency = registrationFee?.currency || "INR";
                        }

                        // If session name already contains amount info, don't add it again
                        const nameContainsAmount =
                          /\([A-Z]{3}\s+[\d,]+\)/i.test(sessionName);

                        return (
                          <div className="summary-row" key={sessionId}>
                            <span
                              className="summary-label"
                              style={{ width: "100%" }}
                            >
                              {
                                nameContainsAmount
                                  ? sessionName // Name already has amount, just show it
                                  : `${sessionName} (${displayCurrency} ${parseFloat(
                                      displayRate || 0
                                    ).toFixed(2)})` // Add amount to name with fixed decimals
                              }
                              {isPaid && (
                                <span
                                  style={{
                                    marginLeft: "8px",
                                    color: "#28a745",
                                    fontSize: "12px",
                                  }}
                                >
                                  ✓ Paid
                                </span>
                              )}
                              {isSaved && !isPaid && (
                                <span
                                  style={{
                                    marginLeft: "8px",
                                    color: "#0d6efd",
                                    fontSize: "12px",
                                  }}
                                >
                                  ✓ Saved
                                </span>
                              )}
                            </span>
                          </div>
                        );
                      })}
                      <div className="summary-row">
                        <span className="summary-label">
                          Subtotal ({allPaidSessionIds.length} session
                          {allPaidSessionIds.length > 1 ? "s" : ""}):
                        </span>
                        <span
                          className="summary-value"
                          style={{ fontWeight: "bold" }}
                        >
                          {(() => {
                            const focusedTotal = allPaidSessionIds.reduce(
                              (sum, sessionId) => {
                                const paidSession = paidFocusedSessions.find(
                                  (ps) => ps.feeTypeId === sessionId
                                );

                                // Use rate from paidSession (DB) if available, otherwise from state
                                let rate = 0;
                                if (paidSession && paidSession.rate) {
                                  // Use rate from database
                                  rate = parseFloat(paidSession.rate);
                                } else {
                                  // Use rate from fetched fee (for new selections)
                                  const sessionFee =
                                    focusedSessionFees[sessionId];
                                  if (sessionFee && sessionFee.rate) {
                                    rate = parseFloat(sessionFee.rate);
                                  }
                                }

                                return sum + rate;
                              },
                              0
                            );
                            const currency = registrationFee
                              ? registrationFee.currency
                              : "INR";
                            const symbol =
                              formData.country === "India" ? "₹" : "$";
                            return `${symbol}${focusedTotal.toFixed(
                              2
                            )} (${currency})`;
                          })()}
                        </span>
                      </div>
                    </div>
                  );
                })()}

                {/* Zero Payments Section */}
                {(() => {
                  // Collect all free items (rate = 0)
                  const freeItems = [];

                  // First, add items from formData.zeroPayments (already saved in database)
                  if (
                    formData.zeroPayments &&
                    Array.isArray(formData.zeroPayments)
                  ) {
                    formData.zeroPayments.forEach((zeroPayment) => {
                      freeItems.push({
                        name: zeroPayment.name,
                        type: "Saved",
                        isPaid: true, // Already saved in zero_payments table
                        createdDate: zeroPayment.createdDate,
                      });
                    });
                  }

                  // Check for free accompanying persons (not yet saved)
                  if (
                    accompanyingPersonFee &&
                    parseFloat(accompanyingPersonFee.rate) === 0
                  ) {
                    const paidAccompanyingPersons = formData.additionalFees
                      ? formData.additionalFees.filter(
                          (fee) => fee.feeTypeId === accompanyingPersonFee.id
                        )
                      : [];
                    const allAccompanyingPersons = [
                      ...paidAccompanyingPersons.map((paid) => ({
                        name: paid.name,
                        isPaid: true,
                      })),
                      ...accompanyingPersons
                        .filter(
                          (person) =>
                            !paidAccompanyingPersons.some(
                              (paid) => paid.name === person.name
                            )
                        )
                        .map((person) => ({
                          name: person.name || "Accompanying Person",
                          isPaid: false,
                        })),
                    ];

                    allAccompanyingPersons.forEach((person) => {
                      // Don't duplicate if already in zeroPayments
                      const alreadyInZeroPayments =
                        formData.zeroPayments &&
                        formData.zeroPayments.some(
                          (zp) => zp.name === person.name
                        );
                      if (!alreadyInZeroPayments) {
                        freeItems.push({
                          name: person.name,
                          type: "Accompanying Person",
                          isPaid: person.isPaid,
                        });
                      }
                    });
                  }

                  // Check for free focused sessions (not yet saved)
                  const paidFocusedSessions = formData.additionalFees
                    ? formData.additionalFees.filter(
                        (fee) =>
                          !accompanyingPersonFee ||
                          fee.feeTypeId !== accompanyingPersonFee.id
                      )
                    : [];
                  const allFocusedSessionIds = [
                    ...paidFocusedSessions.map((fee) => fee.feeTypeId),
                    ...selectedFocusedSessions.filter(
                      (sessionId) =>
                        !paidFocusedSessions.some(
                          (paid) => paid.feeTypeId === sessionId
                        )
                    ),
                  ];

                  allFocusedSessionIds.forEach((sessionId) => {
                    const session = focusedSessions.find(
                      (s) => s.id === sessionId
                    );
                    const sessionFee = focusedSessionFees[sessionId];
                    const isPaid = paidFocusedSessions.some(
                      (paid) => paid.feeTypeId === sessionId
                    );
                    const sessionName = session
                      ? session.value
                      : `Session ${sessionId}`;

                    // Only include if rate is 0
                    if (sessionFee && parseFloat(sessionFee.rate) === 0) {
                      // Don't duplicate if already in zeroPayments
                      const alreadyInZeroPayments =
                        formData.zeroPayments &&
                        formData.zeroPayments.some(
                          (zp) => zp.name === sessionName
                        );
                      if (!alreadyInZeroPayments) {
                        freeItems.push({
                          name: sessionName,
                          type: "Focused Session",
                          isPaid: isPaid,
                        });
                      }
                    }
                  });

                  if (freeItems.length === 0) return null;

                  return (
                    <div
                      className="summary-section"
                      style={{
                        backgroundColor: "#e8f5e9",
                        padding: "15px",
                        marginTop: "10px",
                        borderRadius: "5px",
                        border: "1px solid #4caf50",
                      }}
                    >
                      <h4 style={{ color: "#2e7d32", marginBottom: "10px" }}>
                        <span style={{ marginRight: "8px" }}>🆓</span>
                        Zero Payments (Free Items)
                      </h4>
                      {freeItems.map((item, index) => (
                        <div
                          className="summary-row"
                          key={index}
                          style={{ marginBottom: "8px" }}
                        >
                          <span className="summary-label">
                            {item.name}
                            <span
                              style={{
                                marginLeft: "8px",
                                fontSize: "11px",
                                color: "#666",
                                fontStyle: "italic",
                              }}
                            >
                              ({item.type})
                            </span>
                          </span>
                          <span
                            className="summary-value"
                            style={{ color: "#2e7d32", fontWeight: "bold" }}
                          >
                            FREE
                            {item.isPaid && (
                              <span
                                style={{
                                  marginLeft: "8px",
                                  color: "#28a745",
                                  fontSize: "12px",
                                }}
                              >
                                ✓ Saved
                              </span>
                            )}
                          </span>
                        </div>
                      ))}
                      <div
                        className="summary-row"
                        style={{
                          marginTop: "10px",
                          paddingTop: "10px",
                          borderTop: "1px solid #4caf50",
                        }}
                      >
                        <span
                          className="summary-label"
                          style={{ fontWeight: "bold" }}
                        >
                          Total Free Items:
                        </span>
                        <span
                          className="summary-value"
                          style={{ fontWeight: "bold", color: "#2e7d32" }}
                        >
                          {freeItems.length} item
                          {freeItems.length > 1 ? "s" : ""}
                        </span>
                      </div>
                    </div>
                  );
                })()}

                {/* Total Fee Section */}
                {registrationFee && (
                  <div
                    className="summary-section"
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "15px",
                      marginTop: "10px",
                      borderRadius: "5px",
                    }}
                  >
                    <h4 style={{ marginBottom: "15px" }}>Total Amount</h4>
                    {(() => {
                      const regFee = parseFloat(registrationFee.rate);
                      const currency = registrationFee.currency || "INR";
                      const currencySymbol =
                        currency === "INR"
                          ? "₹"
                          : currency === "USD"
                          ? "$"
                          : currency;

                      // Calculate PAID amounts (only items with paymentId)
                      const existingAdditionalFees =
                        formData.additionalFees || [];

                      // Registration fee - check if paid
                      const registrationPaid = formData.paid ? regFee : 0;

                      // Paid accompanying persons (only those with paymentId)
                      const paidAccompanyingFees = existingAdditionalFees
                        .filter(
                          (fee) =>
                            accompanyingPersonFee &&
                            fee.feeTypeId === accompanyingPersonFee.id &&
                            fee.paymentId !== null &&
                            fee.paymentId !== undefined
                        )
                        .reduce((sum, fee) => {
                          let itemRate = fee.rate ? parseFloat(fee.rate) : 0;
                          // If rate is null/0, get from accompanyingPersonFee
                          if (itemRate === 0 && accompanyingPersonFee) {
                            itemRate = parseFloat(
                              accompanyingPersonFee.rate || 0
                            );
                          }
                          return sum + itemRate;
                        }, 0);

                      // Paid focused sessions (only those with paymentId)
                      const paidFocusedFees = existingAdditionalFees
                        .filter(
                          (fee) =>
                            fee.paymentId !== null &&
                            fee.paymentId !== undefined &&
                            (!accompanyingPersonFee ||
                              fee.feeTypeId !== accompanyingPersonFee.id)
                        )
                        .reduce((sum, fee) => {
                          let itemRate = fee.rate ? parseFloat(fee.rate) : 0;
                          // If rate is null/0, try to get from focusedSessionFees
                          if (
                            itemRate === 0 &&
                            focusedSessionFees[fee.feeTypeId]
                          ) {
                            itemRate = parseFloat(
                              focusedSessionFees[fee.feeTypeId].rate || 0
                            );
                          }
                          return sum + itemRate;
                        }, 0);

                      // For PAID amounts: The convenience fee is already stored in the database as part of the payment
                      // So we should retrieve it from the payment records, not calculate it here
                      // TODO: Backend should return payment breakdown including convenience fee
                      // For now, we'll sum up what we have from additionalFees (which should include the convenience fee row)

                      // Check if we have payment data with breakdown
                      const payments = formData.payment || [];
                      let totalPaidAmount = 0;
                      let paidConvenienceFee = 0;

                      if (payments.length > 0 && payments[0].breakdown) {
                        // If we have payment breakdown from DB, sum all items including convenience fee
                        totalPaidAmount = payments[0].breakdown.reduce(
                          (sum, item) => {
                            const amount = parseFloat(item.amount || 0);
                            if (item.type === "CONVENIENCE_FEE") {
                              paidConvenienceFee += amount;
                            }
                            return sum + amount;
                          },
                          0
                        );
                        console.log(
                          "✅ Using payment breakdown from DB:",
                          totalPaidAmount
                        );
                      } else {
                        // Fallback: Calculate from individual fees (legacy mode)
                        // This means convenience fee was not stored separately, so we calculate it
                        const totalPaidAmountBeforeFee =
                          registrationPaid +
                          paidAccompanyingFees +
                          paidFocusedFees;
                        paidConvenienceFee = totalPaidAmountBeforeFee * 0.0354;
                        totalPaidAmount =
                          totalPaidAmountBeforeFee + paidConvenienceFee;
                        console.log(
                          "⚠️  Using calculated paid amount (no breakdown in DB):",
                          totalPaidAmount
                        );
                      }

                      // Calculate OUTSTANDING amounts (unpaid items)
                      // Registration fee outstanding
                      const registrationOutstanding = formData.paid
                        ? 0
                        : regFee;

                      // ALL items with paymentId === null from additionalFees are OUTSTANDING
                      // Need to calculate their rate from the fee API
                      const outstandingItems = existingAdditionalFees.filter(
                        (fee) => !fee.paymentId
                      );

                      const outstandingFromAdditionalFees =
                        outstandingItems.reduce((sum, fee) => {
                          // If rate is null or missing, calculate it from the fee type
                          let itemRate = fee.rate ? parseFloat(fee.rate) : 0;

                          // If rate is 0 or null, try to get it from accompanyingPersonFee or focusedSessionFees
                          if (
                            itemRate === 0 &&
                            accompanyingPersonFee &&
                            fee.feeTypeId === accompanyingPersonFee.id
                          ) {
                            itemRate = parseFloat(
                              accompanyingPersonFee.rate || 0
                            );
                          } else if (
                            itemRate === 0 &&
                            focusedSessionFees[fee.feeTypeId]
                          ) {
                            itemRate = parseFloat(
                              focusedSessionFees[fee.feeTypeId].rate || 0
                            );
                          }

                          return sum + itemRate;
                        }, 0);

                      console.log(
                        "🔍 Outstanding items from additionalFees:",
                        outstandingItems,
                        "Total outstanding:",
                        outstandingFromAdditionalFees
                      );

                      // Count how many accompanying persons and focused sessions are already in additionalFees
                      const existingAccompanyingCount =
                        existingAdditionalFees.filter(
                          (fee) =>
                            accompanyingPersonFee &&
                            fee.feeTypeId === accompanyingPersonFee.id
                        ).length;

                      const existingFocusedSessionIds = existingAdditionalFees
                        .filter(
                          (fee) =>
                            !accompanyingPersonFee ||
                            fee.feeTypeId !== accompanyingPersonFee.id
                        )
                        .map((fee) => fee.feeTypeId);

                      // Calculate NEW items (not yet in additionalFees at all)
                      // New accompanying persons
                      const newAccompanyingCount = Math.max(
                        0,
                        accompanyingPersons.length - existingAccompanyingCount
                      );
                      const accompanyingRate = accompanyingPersonFee
                        ? parseFloat(accompanyingPersonFee.rate)
                        : 0;
                      const newAccompanyingOutstanding =
                        newAccompanyingCount * accompanyingRate;

                      // New focused sessions
                      const newFocusedSessionIds =
                        selectedFocusedSessions.filter(
                          (sessionId) =>
                            !existingFocusedSessionIds.includes(sessionId)
                        );
                      const newFocusedOutstanding = newFocusedSessionIds.reduce(
                        (sum, sessionId) => {
                          const sessionFee = focusedSessionFees[sessionId];
                          return (
                            sum +
                            (sessionFee && sessionFee.rate
                              ? parseFloat(sessionFee.rate)
                              : 0)
                          );
                        },
                        0
                      );

                      const accompanyingOutstanding =
                        newAccompanyingOutstanding;
                      const focusedOutstanding = newFocusedOutstanding;

                      // TOTAL OUTSTANDING = registration + all items with paymentId null + new items not yet saved
                      const totalOutstandingBeforeFee =
                        registrationOutstanding +
                        outstandingFromAdditionalFees +
                        accompanyingOutstanding +
                        focusedOutstanding;
                      // Add 2.9% convenience fee to outstanding amount
                      const convenienceFeeRate = 0.0354;
                      const convenienceFee =
                        totalOutstandingBeforeFee * convenienceFeeRate;
                      const totalOutstanding =
                        totalOutstandingBeforeFee + convenienceFee;
                      const grandTotal = totalPaidAmount + totalOutstanding;

                      // Debug logging
                      console.log("💰 Total Amount Calculation:", {
                        "formData.paid": formData.paid,
                        registrationFee: regFee,
                        registrationPaid: registrationPaid,
                        registrationOutstanding: registrationOutstanding,
                        existingAdditionalFees: existingAdditionalFees,
                        outstandingFromAdditionalFees:
                          outstandingFromAdditionalFees,
                        newAccompanyingCount: newAccompanyingCount,
                        newAccompanyingOutstanding: newAccompanyingOutstanding,
                        newFocusedSessionIds: newFocusedSessionIds,
                        newFocusedOutstanding: newFocusedOutstanding,
                        totalPaidAmount: totalPaidAmount,
                        totalOutstanding: totalOutstanding,
                        grandTotal: grandTotal,
                      });

                      return (
                        <>
                          {/* If both paid and outstanding exist, show breakdown */}
                          {totalPaidAmount > 0 && totalOutstanding > 0 && (
                            <>
                              <div className="summary-row">
                                <span
                                  className="summary-label"
                                  style={{
                                    fontSize: "1em",
                                    fontWeight: "500",
                                    color: "#28a745",
                                  }}
                                >
                                  Paid Amount (incl. 2.9% fee):
                                </span>
                                <span
                                  className="summary-value"
                                  style={{
                                    fontSize: "1.1em",
                                    fontWeight: "bold",
                                    color: "#28a745",
                                  }}
                                >
                                  {currencySymbol}
                                  {totalPaidAmount.toFixed(2)} ({currency})
                                </span>
                              </div>
                              <div className="summary-row">
                                <span
                                  className="summary-label"
                                  style={{
                                    fontSize: "1em",
                                    fontWeight: "500",
                                    color: "#666",
                                  }}
                                >
                                  Outstanding (before fee):
                                </span>
                                <span
                                  className="summary-value"
                                  style={{
                                    fontSize: "1em",
                                    fontWeight: "500",
                                    color: "#666",
                                  }}
                                >
                                  {currencySymbol}
                                  {totalOutstandingBeforeFee.toFixed(2)} (
                                  {currency})
                                </span>
                              </div>
                              <div className="summary-row">
                                <span
                                  className="summary-label"
                                  style={{
                                    fontSize: "0.95em",
                                    fontWeight: "400",
                                    color: "#666",
                                    fontStyle: "italic",
                                  }}
                                >
                                  Convenience Fee (3.54%):
                                </span>
                                <span
                                  className="summary-value"
                                  style={{
                                    fontSize: "0.95em",
                                    fontWeight: "400",
                                    color: "#666",
                                    fontStyle: "italic",
                                  }}
                                >
                                  {currencySymbol}
                                  {convenienceFee.toFixed(2)} ({currency})
                                </span>
                              </div>
                              <div className="summary-row">
                                <span
                                  className="summary-label"
                                  style={{
                                    fontSize: "1em",
                                    fontWeight: "500",
                                    color: "#dc3545",
                                  }}
                                >
                                  Outstanding Amount:
                                </span>
                                <span
                                  className="summary-value"
                                  style={{
                                    fontSize: "1.1em",
                                    fontWeight: "bold",
                                    color: "#dc3545",
                                  }}
                                >
                                  {currencySymbol}
                                  {totalOutstanding.toFixed(2)} ({currency})
                                </span>
                              </div>
                              <div
                                className="summary-row"
                                style={{
                                  marginTop: "10px",
                                  paddingTop: "10px",
                                  borderTop: "2px solid #dee2e6",
                                }}
                              >
                                <span
                                  className="summary-label"
                                  style={{
                                    fontSize: "1.1em",
                                    fontWeight: "bold",
                                  }}
                                >
                                  Grand Total:
                                </span>
                                <span
                                  className="summary-value"
                                  style={{
                                    fontSize: "1.2em",
                                    fontWeight: "bold",
                                    color: "#007bff",
                                  }}
                                >
                                  {currencySymbol}
                                  {grandTotal.toFixed(2)} ({currency})
                                </span>
                              </div>
                            </>
                          )}

                          {/* If only paid (fully paid scenario) */}
                          {totalPaidAmount > 0 && totalOutstanding === 0 && (
                            <>
                              {paidConvenienceFee > 0 && (
                                <>
                                  <div className="summary-row">
                                    <span
                                      className="summary-label"
                                      style={{
                                        fontSize: "1em",
                                        fontWeight: "500",
                                        color: "#28a745",
                                      }}
                                    >
                                      Subtotal:
                                    </span>
                                    <span
                                      className="summary-value"
                                      style={{
                                        fontSize: "1em",
                                        fontWeight: "500",
                                        color: "#28a745",
                                      }}
                                    >
                                      {currencySymbol}
                                      {(
                                        totalPaidAmount - paidConvenienceFee
                                      ).toFixed(2)}{" "}
                                      ({currency})
                                    </span>
                                  </div>
                                  <div className="summary-row">
                                    <span
                                      className="summary-label"
                                      style={{
                                        fontSize: "0.95em",
                                        fontWeight: "400",
                                        color: "#28a745",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      Convenience Fee (3.54%):
                                    </span>
                                    <span
                                      className="summary-value"
                                      style={{
                                        fontSize: "0.95em",
                                        fontWeight: "400",
                                        color: "#28a745",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      {currencySymbol}
                                      {paidConvenienceFee.toFixed(2)} (
                                      {currency})
                                    </span>
                                  </div>
                                </>
                              )}
                              <div
                                className="summary-row"
                                style={{
                                  marginTop: "10px",
                                  paddingTop: "10px",
                                  borderTop:
                                    paidConvenienceFee > 0
                                      ? "2px solid #dee2e6"
                                      : "none",
                                }}
                              >
                                <span
                                  className="summary-label"
                                  style={{
                                    fontSize: "1.1em",
                                    fontWeight: "bold",
                                    color: "#28a745",
                                  }}
                                >
                                  Total Amount:
                                </span>
                                <span
                                  className="summary-value"
                                  style={{
                                    fontSize: "1.2em",
                                    fontWeight: "bold",
                                    color: "#28a745",
                                  }}
                                >
                                  {currencySymbol}
                                  {totalPaidAmount.toFixed(2)} ({currency}) ✓
                                  Fully Paid
                                </span>
                              </div>
                            </>
                          )}

                          {/* If only outstanding (first-time user or no payment yet) */}
                          {totalPaidAmount === 0 && totalOutstanding > 0 && (
                            <>
                              <div className="summary-row">
                                <span
                                  className="summary-label"
                                  style={{
                                    fontSize: "1em",
                                    fontWeight: "500",
                                    color: "#666",
                                  }}
                                >
                                  Subtotal:
                                </span>
                                <span
                                  className="summary-value"
                                  style={{
                                    fontSize: "1em",
                                    fontWeight: "500",
                                    color: "#666",
                                  }}
                                >
                                  {currencySymbol}
                                  {totalOutstandingBeforeFee.toFixed(2)} (
                                  {currency})
                                </span>
                              </div>
                              <div className="summary-row">
                                <span
                                  className="summary-label"
                                  style={{
                                    fontSize: "0.95em",
                                    fontWeight: "400",
                                    color: "#666",
                                    fontStyle: "italic",
                                  }}
                                >
                                  Convenience Fee (3.54%):
                                </span>
                                <span
                                  className="summary-value"
                                  style={{
                                    fontSize: "0.95em",
                                    fontWeight: "400",
                                    color: "#666",
                                    fontStyle: "italic",
                                  }}
                                >
                                  {currencySymbol}
                                  {convenienceFee.toFixed(2)} ({currency})
                                </span>
                              </div>
                              <div
                                className="summary-row"
                                style={{
                                  marginTop: "10px",
                                  paddingTop: "10px",
                                  borderTop: "2px solid #dee2e6",
                                }}
                              >
                                <span
                                  className="summary-label"
                                  style={{
                                    fontSize: "1.1em",
                                    fontWeight: "bold",
                                    color: "#dc3545",
                                  }}
                                >
                                  Outstanding Amount:
                                </span>
                                <span
                                  className="summary-value"
                                  style={{
                                    fontSize: "1.2em",
                                    fontWeight: "bold",
                                    color: "#dc3545",
                                  }}
                                >
                                  {currencySymbol}
                                  {totalOutstanding.toFixed(2)} ({currency})
                                </span>
                              </div>
                            </>
                          )}
                        </>
                      );
                    })()}
                  </div>
                )}

                {/* Payment Status Section */}
                <div className="summary-section">
                  <h4>Payment Status</h4>

                  {formData.paid ? (
                    <div className="payment-status completed">
                      <span className="status-icon">✅</span>
                      <span className="status-text">
                        Payment completed and registration confirmed
                      </span>
                    </div>
                  ) : (
                    <div className="payment-status pending">
                      <span className="status-icon">⏳</span>
                      <span className="status-text">
                        Payment pending - Please complete payment to confirm
                        registration
                      </span>
                    </div>
                  )}
                </div>

                {/* Terms and Conditions Checkbox */}
                <div
                  className={`terms-checkbox-container ${
                    termsError ? "error" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => {
                      setAgreedToTerms(e.target.checked);
                      if (e.target.checked) {
                        setTermsError("");
                      }
                    }}
                  />
                  <label
                    htmlFor="terms-checkbox"
                    className="terms-checkbox-label"
                  >
                    I agree to the{" "}
                    <span
                      className="terms-link"
                      onClick={(e) => {
                        e.preventDefault();
                        setShowTermsModal(true);
                      }}
                    >
                      Terms & Conditions
                    </span>
                  </label>
                </div>
                {termsError && (
                  <div className="terms-error-message">{termsError}</div>
                )}

                <div className="summary-actions">
                  {/* Registration Status */}
                  {formData.id || formData.regId ? (
                    <div className="registration-status success">
                      ✅ Registration saved successfully!
                      {formData.id && <span> (ID: {formData.id})</span>}
                    </div>
                  ) : (
                    <div className="registration-status warning">
                      ⚠️ Registration not saved yet. Please save before
                      proceeding to payment.
                    </div>
                  )}

                  <div className="summary-buttons">
                    <button
                      type="button"
                      className="back-btn"
                      onClick={() => setStep(4)}
                    >
                      Back
                    </button>
                    {(() => {
                      // Calculate outstanding amount to determine if payment button should be shown
                      const regFee = registrationFee
                        ? parseFloat(registrationFee.rate)
                        : 0;
                      const existingAdditionalFees =
                        formData.additionalFees || [];

                      // Registration fee outstanding
                      const registrationOutstanding = formData.paid
                        ? 0
                        : regFee;

                      // Outstanding from additionalFees (items with paymentId === null)
                      const outstandingItems = existingAdditionalFees.filter(
                        (fee) => !fee.paymentId
                      );
                      const outstandingFromAdditionalFees =
                        outstandingItems.reduce((sum, fee) => {
                          let itemRate = fee.rate ? parseFloat(fee.rate) : 0;
                          if (
                            itemRate === 0 &&
                            accompanyingPersonFee &&
                            fee.feeTypeId === accompanyingPersonFee.id
                          ) {
                            itemRate = parseFloat(
                              accompanyingPersonFee.rate || 0
                            );
                          } else if (
                            itemRate === 0 &&
                            focusedSessionFees[fee.feeTypeId]
                          ) {
                            itemRate = parseFloat(
                              focusedSessionFees[fee.feeTypeId].rate || 0
                            );
                          }
                          return sum + itemRate;
                        }, 0);

                      // New items not yet saved
                      const existingAccompanyingCount =
                        existingAdditionalFees.filter(
                          (fee) =>
                            accompanyingPersonFee &&
                            fee.feeTypeId === accompanyingPersonFee.id
                        ).length;
                      const existingFocusedSessionIds = existingAdditionalFees
                        .filter(
                          (fee) =>
                            !accompanyingPersonFee ||
                            fee.feeTypeId !== accompanyingPersonFee.id
                        )
                        .map((fee) => fee.feeTypeId);

                      const newAccompanyingCount = Math.max(
                        0,
                        accompanyingPersons.length - existingAccompanyingCount
                      );
                      const accompanyingRate = accompanyingPersonFee
                        ? parseFloat(accompanyingPersonFee.rate)
                        : 0;
                      const newAccompanyingOutstanding =
                        newAccompanyingCount * accompanyingRate;

                      const newFocusedSessionIds =
                        selectedFocusedSessions.filter(
                          (sessionId) =>
                            !existingFocusedSessionIds.includes(sessionId)
                        );
                      const newFocusedOutstanding = newFocusedSessionIds.reduce(
                        (sum, sessionId) => {
                          const sessionFee = focusedSessionFees[sessionId];
                          return (
                            sum +
                            (sessionFee && sessionFee.rate
                              ? parseFloat(sessionFee.rate)
                              : 0)
                          );
                        },
                        0
                      );

                      const totalOutstanding =
                        registrationOutstanding +
                        outstandingFromAdditionalFees +
                        newAccompanyingOutstanding +
                        newFocusedOutstanding;

                      // Only show payment button if there's an outstanding amount
                      if (totalOutstanding > 0) {
                        return (
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={async () => {
                              // Check terms agreement first
                              if (!agreedToTerms) {
                                setTermsError(
                                  "You must agree to the Terms & Conditions before proceeding to payment."
                                );
                                const termsCheckbox = document.querySelector(
                                  ".terms-checkbox-container"
                                );
                                if (termsCheckbox) {
                                  termsCheckbox.scrollIntoView({
                                    behavior: "smooth",
                                    block: "center",
                                  });
                                }
                                return;
                              }

                              // Save the registration first
                              const saveSuccess = await saveStep();

                              // If save was successful, proceed to payment
                              if (saveSuccess) {
                                setStep(6);
                              }
                            }}
                            disabled={saving}
                          >
                            {saving ? "Saving..." : "Proceed to Payment →"}
                          </button>
                        );
                      } else {
                        // No outstanding amount - show a completion message instead
                        return (
                          <div className="registration-status success">
                            ✅ All payments completed - No outstanding balance
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>
              </div>
            )}
          </>
        );
      case 6: {
        let displayCurrency = "INR";
        let displayAmount = 0;
        let displaySymbol = "₹";
        let totalAmount = 0;

        // Check if this is an additional payment (base registration already paid)
        const isAdditionalPayment = formData.paid === true;

        if (registrationFee && registrationFee.rate) {
          displayCurrency = registrationFee.currency || "INR";
          displaySymbol = formData.country === "India" ? "₹" : "$";
          // Only include base fee if not already paid
          if (!isAdditionalPayment) {
            totalAmount = parseFloat(registrationFee.rate);
          }
        }

        const existingAdditionalFees = formData.additionalFees || [];

        // CRITICAL: Add outstanding items from additionalFees (items with paymentId === null)
        // These are items that have been saved but not yet paid
        const outstandingItems = existingAdditionalFees.filter(
          (fee) => !fee.paymentId
        );

        console.log("💳 Step 6 Payment Calculation:");
        console.log(
          "  - Outstanding items from additionalFees:",
          outstandingItems
        );

        // Calculate outstanding amounts from saved but unpaid items
        const outstandingFromAdditionalFees = outstandingItems.reduce(
          (sum, fee) => {
            let itemRate = fee.rate ? parseFloat(fee.rate) : 0;

            // If rate is null/0, try to get it from accompanyingPersonFee or focusedSessionFees
            if (
              itemRate === 0 &&
              accompanyingPersonFee &&
              fee.feeTypeId === accompanyingPersonFee.id
            ) {
              itemRate = parseFloat(accompanyingPersonFee.rate || 0);
            } else if (itemRate === 0 && focusedSessionFees[fee.feeTypeId]) {
              itemRate = parseFloat(
                focusedSessionFees[fee.feeTypeId].rate || 0
              );
            }

            console.log(
              `    - Item: ${fee.name} (feeTypeId: ${fee.feeTypeId}), rate: ${itemRate}`
            );
            return sum + itemRate;
          },
          0
        );

        totalAmount += outstandingFromAdditionalFees;

        console.log(
          "  - Outstanding from additionalFees total:",
          outstandingFromAdditionalFees
        );

        // Calculate UNPAID accompanying persons only (from state, not yet saved)
        // Need to exclude those already in additionalFees
        const existingAccompanyingIds = existingAdditionalFees
          .filter(
            (fee) =>
              accompanyingPersonFee &&
              fee.feeTypeId === accompanyingPersonFee.id
          )
          .map((fee) => fee.name); // Use name to match

        if (
          accompanyingPersons.length > 0 &&
          accompanyingPersonFee &&
          accompanyingPersonFee.rate
        ) {
          // Count only unpaid accompanying persons NOT already in database
          const unpaidNewAccompanyingPersons = accompanyingPersons.filter(
            (person) =>
              !person.paid &&
              !person.paymentId &&
              !existingAccompanyingIds.includes(person.name)
          );
          if (unpaidNewAccompanyingPersons.length > 0) {
            const accompanyingTotal =
              parseFloat(accompanyingPersonFee.rate) *
              unpaidNewAccompanyingPersons.length;
            totalAmount += accompanyingTotal;
            console.log(
              "  - New unpaid accompanying persons:",
              unpaidNewAccompanyingPersons.length,
              "total:",
              accompanyingTotal
            );
          }
        }

        // Calculate UNPAID focused sessions only (from state, not yet saved)
        // Need to exclude those already in additionalFees
        const existingFocusedSessionIds = existingAdditionalFees
          .filter(
            (fee) =>
              !accompanyingPersonFee ||
              fee.feeTypeId !== accompanyingPersonFee.id
          )
          .map((fee) => fee.feeTypeId);

        if (selectedFocusedSessions.length > 0) {
          // Only add fees for unpaid sessions NOT already in database
          const unpaidNewSessions = selectedFocusedSessions.filter(
            (sessionId) => !existingFocusedSessionIds.includes(sessionId)
          );

          const newSessionsTotal = unpaidNewSessions.reduce(
            (sum, sessionId) => {
              const sessionFee = focusedSessionFees[sessionId];
              if (
                sessionFee &&
                sessionFee.rate &&
                parseFloat(sessionFee.rate) > 0
              ) {
                return sum + parseFloat(sessionFee.rate);
              }
              return sum;
            },
            0
          );

          totalAmount += newSessionsTotal;
          console.log(
            "  - New unpaid focused sessions:",
            unpaidNewSessions,
            "total:",
            newSessionsTotal
          );
        }

        console.log("  - Total payment amount:", totalAmount);

        displayAmount = totalAmount.toFixed(2);

        return (
          <div className="summary-container">
            <div className="summary-section">
              <h4>Payment Details</h4>

              <div className="summary-row">
                <span className="summary-label">Registration Fee:</span>
                <span className="summary-value">
                  {displaySymbol}
                  {registrationFee && registrationFee.rate
                    ? parseFloat(registrationFee.rate).toFixed(2)
                    : "0.00"}{" "}
                  ({displayCurrency})
                </span>
              </div>

              {/* Show UNPAID accompanying persons breakdown only */}
              {accompanyingPersons.length > 0 &&
                accompanyingPersonFee &&
                (() => {
                  // Filter to only unpaid accompanying persons
                  const unpaidAccompanyingPersons = accompanyingPersons.filter(
                    (p) =>
                      p.feeTypeId === accompanyingPersonFee.id &&
                      !p.paid &&
                      !p.paymentId
                  );
                  if (unpaidAccompanyingPersons.length === 0) return null;
                  return (
                    <>
                      <div className="summary-row">
                        <span className="summary-label">
                          Accompanying Persons (
                          {unpaidAccompanyingPersons.length}):
                        </span>
                        <span className="summary-value">
                          {displaySymbol}
                          {(
                            parseFloat(accompanyingPersonFee.rate) *
                            unpaidAccompanyingPersons.length
                          ).toFixed(2)}{" "}
                          ({displayCurrency})
                        </span>
                      </div>
                      <div className="summary-row">
                        <span className="summary-label"></span>
                        <span
                          className="summary-value"
                          style={{
                            fontSize: "0.9em",
                            color: "#666",
                            fontStyle: "italic",
                          }}
                        >
                          (
                          {unpaidAccompanyingPersons
                            .map((p) => p.name)
                            .join(", ")}
                          )
                        </span>
                      </div>
                    </>
                  );
                })()}

              {/* Show UNPAID items from additionalFees (saved but not paid) */}
              {(() => {
                const existingAdditionalFees = formData.additionalFees || [];

                // Get outstanding items (saved but not paid) excluding accompanying persons
                const outstandingFocusedSessions =
                  existingAdditionalFees.filter(
                    (fee) =>
                      !fee.paymentId &&
                      (!accompanyingPersonFee ||
                        fee.feeTypeId !== accompanyingPersonFee.id)
                  );

                if (outstandingFocusedSessions.length === 0) return null;

                // Calculate total and collect session details
                const sessionDetails = outstandingFocusedSessions.map((fee) => {
                  let itemRate = fee.rate ? parseFloat(fee.rate) : 0;

                  // If rate is null/0, try to get it from focusedSessionFees
                  if (itemRate === 0 && focusedSessionFees[fee.feeTypeId]) {
                    itemRate = parseFloat(
                      focusedSessionFees[fee.feeTypeId].rate || 0
                    );
                  }

                  return {
                    id: fee.id,
                    name: fee.name,
                    rate: itemRate,
                  };
                });

                const outstandingTotal = sessionDetails.reduce(
                  (sum, session) => sum + session.rate,
                  0
                );

                return (
                  <>
                    <div className="summary-row">
                      <span className="summary-label">
                        Outstanding Focused Sessions (
                        {outstandingFocusedSessions.length}):
                      </span>
                      <span className="summary-value">
                        {displaySymbol}
                        {outstandingTotal.toFixed(2)} ({displayCurrency})
                      </span>
                    </div>
                    {sessionDetails.map((session) => (
                      <div className="summary-row" key={session.id}>
                        <span
                          className="summary-label"
                          style={{
                            paddingLeft: "20px",
                            fontSize: "0.9em",
                            color: "#666",
                          }}
                        >
                          • {session.name}
                        </span>
                        <span
                          className="summary-value"
                          style={{ fontSize: "0.9em", color: "#666" }}
                        >
                          {displaySymbol}
                          {session.rate.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </>
                );
              })()}

              {/* Show NEW UNPAID focused sessions from state (not yet saved) */}
              {selectedFocusedSessions.length > 0
                ? (() => {
                    const existingAdditionalFees =
                      formData.additionalFees || [];
                    const existingZeroPayments = formData.zeroPayments || [];

                    // Get IDs of sessions already in database (paid or unpaid)
                    const existingFocusedSessionIds = existingAdditionalFees
                      .filter(
                        (fee) =>
                          !accompanyingPersonFee ||
                          fee.feeTypeId !== accompanyingPersonFee.id
                      )
                      .map((fee) => fee.feeTypeId);

                    // Filter to only NEW unpaid sessions (not in database yet)
                    const newUnpaidSessions = selectedFocusedSessions.filter(
                      (sessionId) =>
                        !existingFocusedSessionIds.includes(sessionId)
                    );

                    if (newUnpaidSessions.length === 0) return null;

                    // Calculate total and collect session details
                    const sessionDetails = newUnpaidSessions.map(
                      (sessionId) => {
                        const sessionFee = focusedSessionFees[sessionId];

                        // Try to get session name from multiple sources
                        let sessionName = null;

                        // 1. Try from focusedSessions array
                        const sessionFromList = focusedSessions.find(
                          (s) => s.id === sessionId
                        );
                        if (sessionFromList) {
                          sessionName = sessionFromList.value;
                        }

                        // 2. Try from additionalFees (might have name stored)
                        if (!sessionName) {
                          const feeRecord = existingAdditionalFees.find(
                            (fee) => fee.feeTypeId === sessionId
                          );
                          if (feeRecord && feeRecord.name) {
                            sessionName = feeRecord.name;
                          }
                        }

                        // 3. Try from zeroPayments
                        if (!sessionName) {
                          const zeroRecord = existingZeroPayments.find(
                            (zp) => zp.feeTypeId === sessionId
                          );
                          if (zeroRecord && zeroRecord.name) {
                            sessionName = zeroRecord.name;
                          }
                        }

                        // 4. Fallback to session ID
                        if (!sessionName) {
                          sessionName = `Session ${sessionId}`;
                        }

                        const rate =
                          sessionFee && sessionFee.rate
                            ? parseFloat(sessionFee.rate)
                            : 0;

                        return {
                          id: sessionId,
                          name: sessionName,
                          rate: rate,
                        };
                      }
                    );

                    const newUnpaidTotal = sessionDetails.reduce(
                      (sum, session) => sum + session.rate,
                      0
                    );

                    return (
                      <>
                        <div className="summary-row">
                          <span className="summary-label">
                            New Focused Sessions ({newUnpaidSessions.length}):
                          </span>
                          <span className="summary-value">
                            {displaySymbol}
                            {newUnpaidTotal.toFixed(2)} ({displayCurrency})
                          </span>
                        </div>
                        {sessionDetails.map((session) => (
                          <div className="summary-row" key={session.id}>
                            <span
                              className="summary-label"
                              style={{
                                paddingLeft: "20px",
                                fontSize: "0.9em",
                                color: "#666",
                              }}
                            >
                              • {session.name}
                            </span>
                            <span
                              className="summary-value"
                              style={{ fontSize: "0.9em", color: "#666" }}
                            >
                              {displaySymbol}
                              {session.rate.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </>
                    );
                  })()
                : null}

              {feeError && (
                <div className="summary-row">
                  <span className="summary-value" style={{ color: "#dc2626" }}>
                    ⚠️ {feeError}
                  </span>
                </div>
              )}

              <div
                className="summary-row"
                style={{
                  marginTop: "1rem",
                  paddingTop: "1rem",
                  borderTop: "2px solid #e5e7eb",
                }}
              >
                <span
                  className="summary-label"
                  style={{ fontSize: "1.1rem", fontWeight: "600" }}
                >
                  Total Amount:
                </span>
                <span
                  className="summary-value"
                  style={{ fontSize: "1.1rem", fontWeight: "600" }}
                >
                  {displaySymbol}
                  {displayAmount} ({displayCurrency})
                </span>
              </div>

              {registrationFee && registrationFee.feeCategory && (
                <div className="summary-row">
                  <span className="summary-label">Fee Type:</span>
                  <span className="summary-value">
                    {registrationFee.feeCategory.value}
                  </span>
                </div>
              )}

              {registrationFee &&
                registrationFee.validFrom &&
                registrationFee.validTo && (
                  <div className="summary-row">
                    <span className="summary-label">Valid Period:</span>
                    <span className="summary-value">
                      {new Date(registrationFee.validFrom).toLocaleDateString()}{" "}
                      - {new Date(registrationFee.validTo).toLocaleDateString()}
                    </span>
                  </div>
                )}
            </div>

            <div className="summary-section">
              <h4>Registrant Information</h4>
              {formData.category && (
                <div className="summary-row">
                  <span className="summary-label">Category:</span>
                  <span className="summary-value">{formData.category}</span>
                </div>
              )}
              {/* {formData.registrationType && (
                <div className="summary-row">
                  <span className="summary-label">Registration Type:</span>
                  <span className="summary-value">
                    {formData.registrationType}
                  </span>
                </div>
              )} */}
              {formData.firstName && formData.lastName && (
                <div className="summary-row">
                  <span className="summary-label">Name:</span>
                  <span className="summary-value">
                    {formData.firstName} {formData.lastName}
                  </span>
                </div>
              )}
              {formData.email && (
                <div className="summary-row">
                  <span className="summary-label">Email:</span>
                  <span className="summary-value">{formData.email}</span>
                </div>
              )}
            </div>

            <div className="summary-actions">
              <div className="summary-buttons">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={startPayment}
                  disabled={
                    processingPayment ||
                    !hasRegistrationId ||
                    paymentInitiated ||
                    feeLoading
                  }
                >
                  {processingPayment
                    ? "Processing Payment..."
                    : paymentInitiated
                    ? "Payment Initiated..."
                    : feeLoading
                    ? "Calculating Fee..."
                    : !hasRegistrationId
                    ? "Save Registration First"
                    : "Pay & Complete Registration"}
                </button>
              </div>
              {!hasRegistrationId && (
                <div
                  className="registration-status warning"
                  style={{ marginTop: "1rem" }}
                >
                  ⚠️ Please go back to the Summary step and save your
                  registration before proceeding with payment.
                </div>
              )}
            </div>
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="page-container">
      <div className="form-container">
        <div className="banner-container">
          {bannerUrl && (
            <div className="banner-container">
              <img src={bannerUrl} alt="Banner" className="banner-image" />
            </div>
          )}
        </div>
        <div className="registration-header">
          <div className="header-content">
            <h2 style={{ color: "#000", fontWeight: "bold" }}>
              Conference Registration
            </h2>
            {isExistingUser && existingUser && (
              <div className="user-info">
                <span
                  className="welcome-text"
                  style={{ color: "#000", fontWeight: "600" }}
                >
                  Welcome back,{" "}
                  <strong>
                    {existingUser.firstName} {existingUser.lastName}
                  </strong>
                  !
                </span>
                <span className="user-email">{existingUser.email}</span>
              </div>
            )}
            {!isExistingUser && prefilledEmail && (
              <div className="user-info">
                <span
                  className="new-user-text"
                  style={{ color: "#000", fontWeight: "600" }}
                >
                  Creating new registration for:{" "}
                  <strong>{prefilledEmail}</strong>
                </span>
              </div>
            )}
          </div>
          {onBackToLogin && (
            <button
              type="button"
              className="btn btn-outline back-to-login"
              onClick={onBackToLogin}
            >
              Back to Login
            </button>
          )}
        </div>

        <ProgressBar step={step} />

        <form className="form-grid" onSubmit={(e) => e.preventDefault()}>
          {renderStepFields()}
        </form>

        {/* Button row - show for steps 1-4 only (step 5 has its own buttons) */}
        {step >= 1 && step <= 4 && (
          <div
            className="button-row"
            style={step === 1 ? { justifyContent: "flex-end" } : {}}
          >
            {step > 1 && (
              <button className="btn btn-secondary" onClick={prev}>
                Prev
              </button>
            )}

            <button
              className="btn btn-primary"
              onClick={saveAndContinue}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save & Continue"}
            </button>
          </div>
        )}
      </div>

      {/* Terms and Conditions Modal */}
      <TermsAndConditions
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
    </div>
  );
};

export default ConferenceRegistration;
